/**
 * PayslipOCRUpload Component
 *
 * Handles single or multiple (up to 3) payslip uploads with OCR extraction.
 * Supports AWS Textract and Sarvam AI providers.
 * When multiple payslips are uploaded, shows a comparison table with all payslips
 * side-by-side + verification column with row-by-row verify/query workflow,
 * plus variance-based analytics and missing month detection.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import axiosInstance from '../../config/axios.config';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import {
    processPayslip,
    processMultiplePayslips,
    validateFile,
    getFieldLabel,
    formatCurrency,
    formatConfidence,
    getConfidenceSeverity
} from '../../services/payslipOCRService';
import { useMasking } from '../../layout/context/maskingcontext';
import { maskFieldValue } from '../../utils/maskingConfig';

const MAX_PAYSLIPS = 3;

// Versioned sessionStorage keys. Bump the version suffix whenever the
// OCR response shape changes incompatibly so cached results from older
// schema versions are auto-invalidated. v2 (2026-06-02): switched OCR
// to forced-tool-use; dynamic earnings[]/deductions[] arrays now drive
// the UI. Old caches lack those arrays and would render via the legacy
// fixed-key path even after the change.
const OCR_DATA_KEY = 'ocr_payslip_data_v2';
const OCR_FILES_KEY = 'ocr_payslip_files_v2';

// All fields to display, grouped by category
// Fields that are auto-calculated from selected earnings/deductions
const AUTO_CALC_FIELDS = ['gross_salary', 'total_earnings', 'net_salary', 'net_pay', 'take_home', 'total_deductions'];

// Earnings fields used to calculate gross/total
const EARNING_FIELDS = [
    'basic_salary', 'hra', 'da', 'fixed_da', 'variable_da', 'special_allowance',
    'executive_allowance', 'conveyance', 'conveyance_allowance', 'transport_allowance',
    'tpa', 'medical_allowance', 'lta', 'shift_allowance', 'washing_allowance',
    'food_allowance', 'telephone_allowance', 'education_allowance', 'overtime_allowance',
    'incentive', 'bonus', 'annual_bonus', 'arrears', 'reimbursement', 'other_allowances',
];

// Deduction fields used to calculate total deductions
const DEDUCTION_FIELDS = [
    'pf_employee', 'pf_employer', 'nps_employee', 'nps_employer', 'professional_tax',
    'income_tax', 'tds', 'esi', 'esi_employee', 'loan_recovery', 'other_deductions',
];

/**
 * Slugify a payslip row label into a stable lowercase key suitable for
 * indexing selectedValues / percentages / verificationStatus state.
 * "House Rent Allowance" → "house_rent_allowance"
 * "PF (Employee Contribution)" → "pf_employee_contribution"
 *
 * Used by the dynamic row builder so per-row state survives re-renders
 * and stays consistent with the legacy fixed-key state shape (which is
 * also lowercase + underscores).
 */
const slugifyLabel = (label) =>
    String(label || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

/**
 * Pure helper to compute NTH (Net Take Home) from any (selectedValues,
 * percentages) pair over the same comparisonData rows. Used by both the
 * main calc memo (Default tab) AND the per-lender-tab save flow.
 * Returns 0 when nothing is selected (no earnings + no deductions).
 *
 * Earning vs deduction is decided by row.kind when present (dynamic mode)
 * and falls back to the EARNING_FIELDS / DEDUCTION_FIELDS allowlists
 * (legacy mode) so existing leads with the fixed-key OCR shape keep
 * working.
 */
const computeNthFromSelections = (comparisonData, selectedValues, percentages) => {
    const earningsByKey = {};
    const deductionsByKey = {};
    comparisonData.forEach(row => {
        if (AUTO_CALC_FIELDS.includes(row.field)) return;
        const selIdx = selectedValues?.[row.field];
        if (selIdx === undefined || selIdx === null) return;
        const rawVal = row[`raw_${selIdx}`];
        if (typeof rawVal !== 'number') return;
        const pct = percentages?.[row.field] !== undefined ? percentages[row.field] : 100;
        const adjusted = Math.round(rawVal * pct / 100);
        const kind = row.kind
            || (EARNING_FIELDS.includes(row.field) ? 'earning'
                : DEDUCTION_FIELDS.includes(row.field) ? 'deduction'
                : null);
        if (kind === 'earning') earningsByKey[row.field] = adjusted;
        else if (kind === 'deduction') deductionsByKey[row.field] = adjusted;
    });
    const totalEarnings = Object.values(earningsByKey).reduce((s, v) => s + v, 0);
    const totalDeductions = Object.values(deductionsByKey).reduce((s, v) => s + v, 0);
    return Math.max(0, totalEarnings - totalDeductions);
};

// Categories that participate in the salary calculation (sticky top panel). Everything else
// is "identity / static" info that doesn't change across payslips — shown once below.
const CALC_CATEGORIES = new Set(['Earnings', 'Deductions', 'Net Pay']);

const FIELD_GROUPS = [
    {
        label: 'Company Info',
        icon: 'pi-building',
        fields: [
            { key: 'company_name' }, { key: 'designation' }, { key: 'department' },
            { key: 'location' }, { key: 'cost_centre' }, { key: 'band_grade' },
        ]
    },
    {
        label: 'Earnings',
        icon: 'pi-plus-circle',
        currency: true,
        fields: [
            { key: 'basic_salary' }, { key: 'hra' }, { key: 'da' }, { key: 'fixed_da' },
            { key: 'variable_da' }, { key: 'special_allowance' }, { key: 'executive_allowance' },
            { key: 'conveyance' }, { key: 'conveyance_allowance' }, { key: 'transport_allowance' },
            { key: 'tpa' }, { key: 'medical_allowance' }, { key: 'lta' },
            { key: 'shift_allowance' }, { key: 'washing_allowance' }, { key: 'food_allowance' },
            { key: 'telephone_allowance' }, { key: 'education_allowance' },
            { key: 'overtime_allowance' }, { key: 'incentive' }, { key: 'bonus' },
            { key: 'annual_bonus' }, { key: 'arrears' }, { key: 'reimbursement' },
            { key: 'other_allowances' },
        ]
    },
    {
        label: 'Deductions',
        icon: 'pi-minus-circle',
        currency: true,
        fields: [
            { key: 'pf_employee' }, { key: 'pf_employer' }, { key: 'nps_employee' },
            { key: 'nps_employer' }, { key: 'professional_tax' }, { key: 'income_tax' },
            { key: 'tds' }, { key: 'esi' }, { key: 'esi_employee' },
            { key: 'loan_recovery' }, { key: 'other_deductions' },
        ]
    },
    {
        label: 'Net Pay',
        icon: 'pi-wallet',
        currency: true,
        fields: [
            { key: 'total_deductions', autoCalc: true },
            { key: 'gross_salary', autoCalc: true }, { key: 'total_earnings', autoCalc: true },
            { key: 'net_salary', autoCalc: true }, { key: 'net_pay', autoCalc: true },
        ]
    },
    {
        label: 'Pay Period',
        icon: 'pi-calendar',
        fields: [
            { key: 'pay_period' }, { key: 'payable_days' }, { key: 'tax_regime' },
        ]
    },
    {
        label: 'Employee Info',
        icon: 'pi-user',
        fields: [
            { key: 'employee_name' }, { key: 'employee_id' }, { key: 'father_name' },
            { key: 'gender' }, { key: 'date_of_birth' }, { key: 'date_of_joining' },
            { key: 'date_of_retirement' }, { key: 'gratuity_start_date' },
        ]
    },
    {
        label: 'Identity Numbers',
        icon: 'pi-id-card',
        fields: [
            { key: 'aadhaar_number' }, { key: 'pan_number' }, { key: 'uan_number' },
            { key: 'pf_number' }, { key: 'esi_number' }, { key: 'eps_number' },
            { key: 'pran_number' }, { key: 'saf_number' },
        ]
    },
    {
        label: 'Bank Info',
        icon: 'pi-credit-card',
        fields: [
            { key: 'bank_name' }, { key: 'bank_account_number' }, { key: 'bank_account_last4' },
        ]
    },
    {
        label: 'Employer Contributions',
        icon: 'pi-briefcase',
        currency: true,
        fields: [
            { key: 'employer_pf_contribution' }, { key: 'employer_nps_contribution' },
            { key: 'super_annuation' },
        ]
    },
];

const PayslipOCRUpload = ({
    documentDefinition,
    uploadedFiles = [],
    isUploading = false,
    uploadProgress = 0,
    onUpload,
    onOCRComplete,
    onFullOCRData,  // New: callback with full OCR data {extracted_data, field_mappings, confidence_scores, individual_results, ...}
    onRemove,
    toast,
    disabled = false,
    savedOcrData = null,  // Previously saved OCR data to restore "View Data" state
    hideUpload = false,   // Hide file upload when max files reached, but keep View buttons
    onAutoCreateLead = null, // Callback to auto-create lead after OCR — skips showing OCR dialog
    // When true, render the extraction view INLINE (no Dialog modal) — used when this
    // component lives inside a Sidebar/drawer so we don't stack a 2nd modal on top.
    // Side-effects: hides the "View Data" button (extraction is always visible inline)
    // and the inline render auto-shows once OCR completes.
    inlineExtraction = false,
    // Optional lead id — when present, the backend OCR endpoint persists the
    // file to S3 + lead.payslip_paths + lead.ocr_payslip_details so the View
    // Payslip button works after lead reload (edit mode). Without it, the
    // file is OCR'd in-memory and discarded (legacy behavior, used when the
    // lead hasn't been created yet).
    leadId = null,
    // Optional loan product id — feeds the per-lender NTH tab strip's
    // "+ Add Lender" dropdown. When present, dropdown shows only lenders
    // that have ranges for this product. When null, dropdown stays empty.
    loanProductId = null,
}) => {
    const { isMasked } = useMasking();
    const fileUploadRef = useRef(null);
    // Use refs for callbacks to avoid stale closures in long-running async processFilesWithOCR
    const onFullOCRDataRef = useRef(onFullOCRData);
    const onOCRCompleteRef = useRef(onOCRComplete);
    const onUploadRef = useRef(onUpload);
    useEffect(() => { onFullOCRDataRef.current = onFullOCRData; }, [onFullOCRData]);
    useEffect(() => { onOCRCompleteRef.current = onOCRComplete; }, [onOCRComplete]);
    useEffect(() => { onUploadRef.current = onUpload; }, [onUpload]);

    // Mirror the latest individualResults + ocrResult into refs so the
    // append-mode merge inside processFilesWithOCR always sees the freshest
    // state, even though processFilesWithOCR's useCallback deps array doesn't
    // include them. Without this, uploading payslip 3 after payslip 2 would
    // make the merge see individualResults=[] (the stale closure), and
    // payslip 3 would REPLACE rather than append — which is the bug the
    // user just reported.
    const individualResultsRef = useRef([]);
    const ocrResultRef = useRef(null);
    // Forward ref to processFilesWithOCR so handleFileSelect (declared earlier
    // in the component) can invoke it without a TDZ reference. Provider-choice
    // dialog removed; AWS Textract is now the default and only path.
    const processFilesWithOCRRef = useRef(null);

    const [ocrProcessing, setOcrProcessing] = useState(false);
    // Seconds elapsed while OCR is running. Textract emits no progress during the
    // read (~60s+), so without a live counter the stepper looks frozen. Ticking
    // seconds is the cheapest honest signal that work is still happening.
    const [ocrElapsedSec, setOcrElapsedSec] = useState(0);
    const [ocrProgress, setOcrProgress] = useState(0);
    // Stepped progress indicator (Uploading → Analysing → Data Fetching →
    // Data Formation). Driven directly by the OCR pipeline lifecycle so each
    // stage is observably distinct — deriving stage from `ocrProgress`
    // alone made step 1 (Analysing) flash too briefly to see and step 4
    // (Data Formation) get cut by the resetting `setOcrProgress(0)` in the
    // finally block. Stays visible briefly after completion so the user
    // actually sees step 4 light up green.
    const [stepperStage, setStepperStage] = useState(null);

    // Drive the elapsed-seconds counter off ocrProcessing. Resets to 0 on start,
    // ticks every second while running, and stops (cleanup) when processing ends.
    useEffect(() => {
        if (!ocrProcessing) { setOcrElapsedSec(0); return; }
        const started = Date.now();
        const id = setInterval(() => {
            setOcrElapsedSec(Math.floor((Date.now() - started) / 1000));
        }, 1000);
        return () => clearInterval(id);
    }, [ocrProcessing]);

    const [ocrResult, setOcrResult] = useState(() => {
        // Restore from saved data if available (e.g. when navigating back to this step)
        if (savedOcrData?.extracted_data) {
            return {
                extracted_data: savedOcrData.extracted_data,
                field_mappings: savedOcrData.field_mappings || {},
                confidence_scores: savedOcrData.confidence_scores || {},
                processing_time_ms: savedOcrData.processing_time_ms || 0,
                files_processed: savedOcrData.files_processed || 1,
            };
        }
        return null;
    });
    const [ocrError, setOcrError] = useState(null);
    const [showOcrDialog, setShowOcrDialog] = useState(false);
    // Whether the upload box + status preamble are expanded. Defaults CLOSED so
    // the comparison table — the thing the operator actually works in — starts at
    // the top of the panel. The empty state doesn't use this flag at all (the full
    // drop zone renders on `!ocrResult`), so first-time upload is unaffected.
    const [uploadPanelOpen, setUploadPanelOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [showPayslipViewer, setShowPayslipViewer] = useState(false);
    const [viewingPayslipIndex, setViewingPayslipIndex] = useState(0);
    const [s3BlobUrls, setS3BlobUrls] = useState({}); // Cache: preview URL → blob URL
    const [loadingS3Doc, setLoadingS3Doc] = useState(false);
    // URLs we've tried and failed on. Prevents the render-body fetch fallback
    // from looping forever when a fetch fails for any reason (network, 401,
    // 404, CORS, etc.). Added 2026-05-22 after the looping fetch produced
    // hundreds of console errors when S3 CORS blocked direct fetches.
    // useRef (not useState) because we don't want a failed fetch to trigger
    // a re-render that immediately re-attempts the same failed URL.
    const failedFetchUrls = useRef(new Set());

    // Centralized fetch-as-blob helper used by all 4 preview call sites.
    // - Uses axiosInstance so the auth bearer + cookie go with the request
    //   (the proxy endpoint requires auth:sanctum, and the raw browser fetch
    //   doesn't carry the bearer header).
    // - Marks the URL as "failed" on any error so retries via render-body
    //   are blocked (one failure → stay failed until a new URL appears).
    // - Returns the blob URL on success, null on failure.
    const fetchDocumentBlob = useCallback(async (proxyUrl) => {
        if (failedFetchUrls.current.has(proxyUrl)) return null;
        try {
            const res = await axiosInstance.get(proxyUrl, { responseType: 'blob' });
            return URL.createObjectURL(res.data);
        } catch (err) {
            failedFetchUrls.current.add(proxyUrl);
            console.warn('[PayslipOCRUpload] document preview fetch failed', { proxyUrl, status: err?.response?.status });
            return null;
        }
    }, []);

    // Open a payslip in a new tab. S3-backed payslips need the auth header
    // (the proxy endpoint is gated by auth:sanctum), and window.open() does
    // a fresh request from the new tab that doesn't carry Sanctum's bearer
    // token from localStorage — Laravel sees no auth and redirects to the
    // /login web route. The fix: fetch as blob (via axios, which carries
    // auth), then open the blob URL.
    //
    // For data: or blob: URLs (legacy payslip sources before S3 storage),
    // window.open works directly without auth.
    const openPayslipInNewTab = useCallback(async (ps) => {
        if (!ps?.url) return;
        if (ps.source === 's3') {
            // Authenticated proxy URL — fetch as blob (sends bearer token)
            // then open the blob in a new tab. window.open() can't carry the
            // Sanctum bearer from localStorage, so a direct open would 401.
            const blobUrl = await fetchDocumentBlob(ps.url);
            if (blobUrl) {
                window.open(blobUrl, '_blank');
            } else {
                console.warn('[PayslipOCRUpload] could not open payslip — blob fetch failed');
            }
            return;
        }
        // 'signed-url' — pre-signed S3 URL from getFullDetails. The signed
        // URL grants browser-level read access for the ~1h validity window
        // without needing any Sanctum header, so window.open works directly.
        window.open(ps.url, '_blank');
    }, [fetchDocumentBlob]);

    // Open MANY payslips at once without tripping the popup blocker.
    //
    // Why this isn't just `payslips.forEach(openPayslipInNewTab)`: the s3
    // path awaits fetchDocumentBlob before calling window.open(). By the time
    // the 2nd/3rd promise resolves, the click handler has long since
    // returned — Chrome/Safari no longer treat the window.open() as a user
    // gesture and silently block every tab except the first. Result: only 1
    // of N payslips actually opens.
    //
    // Fix: open N blank tabs SYNCHRONOUSLY inside the click handler (still a
    // user gesture, so the browser allows them), then navigate each one to
    // its real URL after the async blob fetch resolves. Same pattern used
    // in AvailableOffers.js + RegenerateOffersDialog.js. Tabs that the
    // browser refused to pre-open fall back to a direct window.open() with
    // the resolved URL — may still be blocked, but at least the click
    // happened recently enough that some browsers allow it.
    const openAllPayslipsInNewTabs = useCallback(async (payslips) => {
        if (!Array.isArray(payslips) || payslips.length === 0) return;

        // Step 1 (synchronous, inside the click): one blank tab per payslip.
        const preopened = payslips.map(() => {
            try { return window.open('about:blank', '_blank'); } catch { return null; }
        });

        // Step 2 (async): resolve each payslip's URL, navigate its pre-opened
        // tab. We run these in parallel so 3 payslips don't take 3x as long.
        await Promise.all(payslips.map(async (ps, idx) => {
            const tab = preopened[idx];
            try {
                let target = ps?.url;
                if (ps?.source === 's3') {
                    const blobUrl = await fetchDocumentBlob(ps.url);
                    if (!blobUrl) {
                        console.warn('[PayslipOCRUpload] blob fetch failed for payslip', idx);
                        if (tab && !tab.closed) { try { tab.close(); } catch {} }
                        return;
                    }
                    target = blobUrl;
                }
                if (!target) {
                    if (tab && !tab.closed) { try { tab.close(); } catch {} }
                    return;
                }
                if (tab && !tab.closed) {
                    tab.location.href = target;
                } else {
                    // Pre-open was blocked/closed — try a direct open as a
                    // last-resort. May also be blocked, but worth trying.
                    window.open(target, '_blank');
                }
            } catch (err) {
                console.warn('[PayslipOCRUpload] failed to open payslip', idx, err);
                if (tab && !tab.closed) { try { tab.close(); } catch {} }
            }
        }));
    }, [fetchDocumentBlob]);

    // Persisted payslip data URLs (base64) — survive re-mounts via sessionStorage.
    //
    // sessionStorage is meant to bridge a transient re-mount on the SAME lead
    // (the wizard reorders steps, the parent unmounts and re-mounts us, etc).
    // It should NOT leak across leads. Without this gate, uploading a payslip
    // on Lead A and then navigating to "Create New Lead" would show a phantom
    // "1 payslip uploaded" + View Payslip on the new lead, because the
    // sessionStorage entry from Lead A is still there.
    //
    // Restore only when:
    //   (a) we have a leadId — caller is re-opening an existing lead, OR
    //   (b) savedOcrData carries individual_results — caller is restoring
    //       an in-progress session for this same lead.
    // Otherwise we're on a brand-new Create New Lead with nothing to restore,
    // so clear stale storage and start empty.
    const [payslipDataUrls, setPayslipDataUrls] = useState(() => {
        const shouldRestore = !!leadId
            || (savedOcrData && Array.isArray(savedOcrData.individual_results) && savedOcrData.individual_results.length > 0);
        if (!shouldRestore) {
            try { sessionStorage.removeItem(OCR_FILES_KEY); } catch {}
            try { sessionStorage.removeItem(OCR_DATA_KEY); } catch {}
            return [];
        }
        try {
            const cached = sessionStorage.getItem(OCR_FILES_KEY);
            if (cached) return JSON.parse(cached);
        } catch {}
        return [];
    });

    // Convert File objects to data URLs and persist to sessionStorage after OCR success
    // Store BOTH data URL (for sessionStorage persistence) AND file for blob URL creation
    const persistFilesToSession = useCallback(async (files) => {
        const results = await Promise.all(files.map(f => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve({
                url: reader.result,  // data URL for sessionStorage
                name: f.name,
                type: f.type,
                _file: f,  // keep original file for blob URL (not persisted)
            });
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(f);
        })));
        const valid = results.filter(Boolean);
        setPayslipDataUrls(valid);
        // Only persist the data URL + metadata (not the File object)
        try {
            sessionStorage.setItem(OCR_FILES_KEY, JSON.stringify(
                valid.map(({ _file, ...rest }) => rest)
            ));
        } catch {}
    }, []);

    // Helper: convert data URL to Blob URL (works in iframes under strict CSP)
    const dataUrlToBlobUrl = (dataUrl, type) => {
        try {
            const arr = dataUrl.split(',');
            const mime = (arr[0].match(/:(.*?);/) || [])[1] || type || 'application/pdf';
            const bstr = atob(arr[1]);
            const u8arr = new Uint8Array(bstr.length);
            for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
            return URL.createObjectURL(new Blob([u8arr], { type: mime }));
        } catch (e) {
            return dataUrl; // fallback to data URL on error
        }
    };

    // Build viewable payslips: S3-backed (proxy URL) → persisted data URLs.
    //
    // For S3-uploaded files we DON'T expose the raw signed S3 URL to the browser
    // anymore — that approach hit two browser walls:
    //   - CSP refused to iframe a cross-origin PDF
    //   - CORS on the bucket rejected fetch() from our origin
    // Instead we point at our own backend proxy endpoint which streams the file
    // inline (same-origin, our CORS, no signed-URL exposure). The proxy needs
    // both the leadId and the documentId — we read documentId from the upload
    // record's `id` field.
    const viewablePayslips = useMemo(() => {
        // The View Payslip button surfaces ONLY when the file is actually in
        // S3. The previous implementation had a 2nd branch that fell back to
        // sessionStorage blob URLs (browser-cached File bytes) when neither
        // lead_documents nor lead.payslip_paths had anything. That fallback
        // hid real upload failures: a DSA whose POST /documents/upload 422'd
        // would still see "View Payslip" because the bytes were cached in
        // their tab, while colleagues / other tenants / a different browser
        // session saw nothing (since the file genuinely wasn't in S3). We
        // removed that fallback — if the file isn't in S3, no button, and
        // the DSA gets honest feedback to re-upload.

        // 1. S3-uploaded files via the modern /documents/upload path. Records
        // carry `id` (the lead_documents row id) which the backend proxy URL
        // uses to stream the file inline with auth.
        if (uploadedFiles.length > 0 && uploadedFiles.some(f => f.id)) {
            // Build an ABSOLUTE URL using axiosInstance's baseURL. Two reasons:
            //   1. fetchDocumentBlob() passes the URL to axiosInstance.get().
            //      axios uses the URL as-is when absolute, OR prefixes with
            //      baseURL when relative. Mixing the two has bitten us
            //      before (the previous "${apiBase}/api/..." produced a
            //      doubled "/api/api/..." 404 — REACT_APP_API_URL is already
            //      "/api", same as what axiosInstance carries).
            //   2. window.open() (the "View Payslip" button) needs a full
            //      URL or relative-to-document, NOT relative-to-axios-base.
            //      Building it once here keeps both call sites correct.
            const apiBase = axiosInstance.defaults.baseURL || '';
            return uploadedFiles
                .filter(f => f.id) // skip records without an id (shouldn't happen post-upload)
                .map(f => ({
                    // Hit our proxy, not S3 directly. leadId must be present —
                    // if it isn't, the fetch will 404 (we accept that as a clean
                    // failure mode rather than silently falling back to the raw
                    // S3 URL which would just bring the CORS spam back).
                    url: `${apiBase}/offer-calculation/leads/${leadId}/documents/${f.id}/preview`,
                    name: f.original_filename || f.name || 'Payslip',
                    source: 's3',
                    documentId: f.id,
                    mimeType: f.mime_type || null,
                }));
        }

        // 2. Direct signed-URL fallback — used when the lead carries payslips
        // on `lead.payslip_paths` (legacy column populated by WhatsApp Flow or
        // a claimed broadcasted lead where the OCR upload happened in another
        // user's browser session). The wizard maps those into uploadedFiles
        // as {url, original_filename, name} objects WITHOUT an `id` — branch 1
        // can't surface them (no document_id for the proxy URL). The signed
        // S3 URL is valid for ~1 hour and is safe to open directly: it's
        // already signed by the backend in getFullDetails().
        if (uploadedFiles.length > 0) {
            const directUrlFiles = uploadedFiles
                .filter(f => !f.id && typeof f.url === 'string' && f.url.startsWith('http'));
            if (directUrlFiles.length > 0) {
                return directUrlFiles.map(f => ({
                    url: f.url,
                    name: f.original_filename || f.name || 'Payslip',
                    source: 'signed-url',
                    mimeType: f.mime_type || null,
                }));
            }
        }

        return [];
    }, [uploadedFiles, leadId]);

    // (Removed: blob URL cleanup effect. The sessionStorage source that
    // generated those blob URLs was removed — viewablePayslips now only
    // contains http(s) URLs from S3 / our proxy, which don't need explicit
    // revoke() to free memory.)
    const [individualResults, setIndividualResults] = useState(() => {
        // Restore individual results from saved data
        if (savedOcrData?.individual_results && savedOcrData.individual_results.length > 0) {
            // Ensure each item has success flag (may have been lost during serialization)
            const restored = savedOcrData.individual_results.map(r => ({
                ...r,
                success: r.success !== undefined ? r.success : (r.extracted_data ? true : false),
            }));
            return restored;
        }
        return [];
    });

    // Mirror to refs so processFilesWithOCR's append-mode merge always reads
    // the freshest values. See the ref declarations near the top for the
    // full rationale.
    useEffect(() => { individualResultsRef.current = individualResults; }, [individualResults]);
    useEffect(() => { ocrResultRef.current = ocrResult; }, [ocrResult]);

    // Row-by-row verification state: { [fieldKey]: { status: 'verified' | 'query' | null, queryText: '' } }
    const [verificationStatus, setVerificationStatus] = useState(() => {
        // Try savedOcrData prop first
        if (savedOcrData?.verification_status && Object.keys(savedOcrData.verification_status).length > 0) {
            return savedOcrData.verification_status;
        }
        // Fallback: try sessionStorage directly
        try {
            const cached = sessionStorage.getItem(OCR_DATA_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed?.verification_status && Object.keys(parsed.verification_status).length > 0) {
                    return parsed.verification_status;
                }
            }
        } catch {}
        return {};
    });
    // Counter to force DataTable re-render on status changes (not on text typing)
    const [verificationVersion, setVerificationVersion] = useState(0);

    // Value selection & percentage state for comparison table
    // selectedValues: { [fieldKey]: payslipIndex (0, 1, or 2) }
    const [selectedValues, setSelectedValues] = useState(() => {
        if (savedOcrData?.selected_values && Object.keys(savedOcrData.selected_values).length > 0) {
            return savedOcrData.selected_values;
        }
        return {};
    });
    // percentages: { [fieldKey]: number (0-100) }
    const [percentages, setPercentages] = useState(() => {
        if (savedOcrData?.percentages && Object.keys(savedOcrData.percentages).length > 0) {
            return savedOcrData.percentages;
        }
        return {};
    });
    const [globalPercentage, setGlobalPercentage] = useState(100);

    // Per-lender NTH tabs. Each lender tab is its own slice of selections /
    // percentages — Default tab (index 0) keeps the existing selectedValues /
    // percentages state; lender tabs (index 1..N) store their state inside
    // lenderTabs[]. On tab switch we DON'T copy — the calc table reads from
    // selectedValues + percentages, so when active tab > 0 the click handlers
    // route into the active lender's slice via setActiveTabSelections().
    // lenderTabs = [{ lender_id, lender_name, selected_values, percentages }]
    const [lenderTabs, setLenderTabs] = useState(() => {
        const saved = savedOcrData?.lender_nth_selections;
        if (!saved || typeof saved !== 'object') return [];
        return Object.entries(saved).map(([lender_id, entry]) => ({
            lender_id,
            lender_name: entry.lender_name || 'Lender',
            selected_values: entry.selected_values || {},
            percentages: entry.percentages || {},
        }));
    });
    const [activeTabIndex, setActiveTabIndex] = useState(0); // 0 = Default
    const [availableLenders, setAvailableLenders] = useState([]);
    const [loadingLenders, setLoadingLenders] = useState(false);
    const [lendersLoaded, setLendersLoaded] = useState(false);

    // Field keys whose identity-card per-payslip diff view is currently expanded.
    // Used by the "Values differ" toggle in the identity Accordion section.
    const [expandedIdentityDiffs, setExpandedIdentityDiffs] = useState(() => new Set());
    const toggleIdentityDiff = useCallback((fieldKey) => {
        setExpandedIdentityDiffs(prev => {
            const next = new Set(prev);
            if (next.has(fieldKey)) next.delete(fieldKey); else next.add(fieldKey);
            return next;
        });
    }, []);

    // Snapshot of Default-tab selections, used when user is editing a lender
    // tab so the Default state isn't overwritten by lender clicks.
    const defaultTabSelectionsRef = useRef({ selectedValues: {}, percentages: {} });

    // Switch the active tab. Persist the OUTGOING tab's selections back to its
    // slice, then load the INCOMING tab's selections into selectedValues +
    // percentages so the calc table renders the right view. The calc table
    // itself uses selectedValues / percentages directly — no other component
    // change needed.
    const switchToTab = useCallback((nextIndex) => {
        if (nextIndex === activeTabIndex) return;

        // Persist outgoing tab's state
        if (activeTabIndex === 0) {
            defaultTabSelectionsRef.current = { selectedValues, percentages };
        } else {
            const outgoingLenderIdx = activeTabIndex - 1;
            setLenderTabs(prev => prev.map((t, i) => i === outgoingLenderIdx
                ? { ...t, selected_values: selectedValues, percentages }
                : t));
        }

        // Load incoming tab's state
        if (nextIndex === 0) {
            setSelectedValues(defaultTabSelectionsRef.current.selectedValues || {});
            setPercentages(defaultTabSelectionsRef.current.percentages || {});
        } else {
            const incomingTab = lenderTabs[nextIndex - 1];
            if (incomingTab) {
                setSelectedValues(incomingTab.selected_values || {});
                setPercentages(incomingTab.percentages || {});
            }
        }

        setActiveTabIndex(nextIndex);
    }, [activeTabIndex, selectedValues, percentages, lenderTabs]);

    const addLenderTab = useCallback((lender) => {
        if (!lender || !lender.id) return;
        // Skip if already added
        if (lenderTabs.some(t => t.lender_id === lender.id)) return;

        // Persist current tab's state first
        if (activeTabIndex === 0) {
            defaultTabSelectionsRef.current = { selectedValues, percentages };
        } else {
            const outgoingLenderIdx = activeTabIndex - 1;
            setLenderTabs(prev => prev.map((t, i) => i === outgoingLenderIdx
                ? { ...t, selected_values: selectedValues, percentages }
                : t));
        }

        // New lender tabs inherit Default's selections as a starting point so
        // the DSA only has to untick fields the lender excludes (most common
        // case). They can re-tick anything Default left unticked too.
        const seed = defaultTabSelectionsRef.current.selectedValues
            && Object.keys(defaultTabSelectionsRef.current.selectedValues).length
            ? defaultTabSelectionsRef.current
            : { selectedValues, percentages };

        const newTab = {
            lender_id: lender.id,
            lender_name: lender.name || lender.brand_name || lender.legal_name || 'Lender',
            selected_values: { ...(seed.selectedValues || {}) },
            percentages: { ...(seed.percentages || {}) },
        };
        setLenderTabs(prev => [...prev, newTab]);

        // Switch to the newly added tab
        setSelectedValues(newTab.selected_values);
        setPercentages(newTab.percentages);
        setActiveTabIndex(lenderTabs.length + 1);
    }, [activeTabIndex, selectedValues, percentages, lenderTabs]);

    const removeLenderTab = useCallback((lenderIdx) => {
        const targetActive = activeTabIndex;
        setLenderTabs(prev => prev.filter((_, i) => i !== lenderIdx));
        // Adjust active index after removal
        if (targetActive === lenderIdx + 1) {
            // The active tab was the one being removed — fall back to Default
            setSelectedValues(defaultTabSelectionsRef.current.selectedValues || {});
            setPercentages(defaultTabSelectionsRef.current.percentages || {});
            setActiveTabIndex(0);
        } else if (targetActive > lenderIdx + 1) {
            // Active tab was after the removed one — shift index down
            setActiveTabIndex(targetActive - 1);
        }
    }, [activeTabIndex]);

    // Load lenders on first dropdown open (lazy). Uses the existing tenant
    // endpoint /loan-products/{productId}/lenders which returns lenders that
    // have ranges for the borrower's selected loan product — keeps the tab
    // strip focused on lenders that can actually generate offers for this lead.
    // Requires loanProductId prop; without it the dropdown stays empty.
    const ensureLendersLoaded = useCallback(async () => {
        if (lendersLoaded || loadingLenders) return;
        if (!loanProductId) {
            setLendersLoaded(true); // Don't retry every open
            return;
        }
        setLoadingLenders(true);
        try {
            const res = await axiosInstance.get(`/offer-calculation/workflow/loan-products/${loanProductId}/lenders`);
            const list = res?.data?.data || [];
            setAvailableLenders(Array.isArray(list) ? list : []);
            setLendersLoaded(true);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to load lenders:', e?.message || 'Unknown error');
        } finally {
            setLoadingLenders(false);
        }
    }, [lendersLoaded, loadingLenders, loanProductId]);

    // Sync from savedOcrData when prop changes after mount (handles delayed parent state updates)
    useEffect(() => {
        if (!savedOcrData) return;

        // Restore ocrResult if not already set
        if (savedOcrData.extracted_data && !ocrResult) {
            setOcrResult({
                extracted_data: savedOcrData.extracted_data,
                field_mappings: savedOcrData.field_mappings || {},
                confidence_scores: savedOcrData.confidence_scores || {},
                processing_time_ms: savedOcrData.processing_time_ms || 0,
                files_processed: savedOcrData.files_processed || 1,
            });
        }

        // Restore individualResults if not already set
        if (savedOcrData.individual_results && savedOcrData.individual_results.length > 0) {
            setIndividualResults(prev => {
                if (prev.length > 0) return prev; // Don't overwrite existing data
                const restored = savedOcrData.individual_results.map(r => ({
                    ...r,
                    success: r.success !== undefined ? r.success : (r.extracted_data ? true : false),
                }));
                return restored;
            });
        }

        // Restore verification status if not already set
        if (savedOcrData.verification_status && Object.keys(savedOcrData.verification_status).length > 0) {
            setVerificationStatus(prev => {
                if (Object.keys(prev).length > 0) return prev; // Don't overwrite user's current work
                return savedOcrData.verification_status;
            });
            setVerificationVersion(v => v + 1); // Force DataTable re-render
        }

        // Restore selected values & percentages
        if (savedOcrData.selected_values && Object.keys(savedOcrData.selected_values).length > 0) {
            setSelectedValues(prev => Object.keys(prev).length > 0 ? prev : savedOcrData.selected_values);
            // Keep Default-tab ref in sync with restored values so a later
            // tab switch + Use Values writes the right "default" back out.
            defaultTabSelectionsRef.current = {
                selectedValues: savedOcrData.selected_values,
                percentages: savedOcrData.percentages || {},
            };
        }
        if (savedOcrData.percentages && Object.keys(savedOcrData.percentages).length > 0) {
            setPercentages(prev => Object.keys(prev).length > 0 ? prev : savedOcrData.percentages);
        }

        // Restore lender tabs from saved selections. Runs whenever savedOcrData
        // changes — covers the case where ocr_payslip_details is populated
        // AFTER dialog mount (lazy load from the lead). Doesn't overwrite a
        // user's in-progress tabs.
        if (savedOcrData.lender_nth_selections
            && typeof savedOcrData.lender_nth_selections === 'object'
            && Object.keys(savedOcrData.lender_nth_selections).length > 0) {
            setLenderTabs(prev => {
                if (prev.length > 0) return prev; // user already configured tabs this session
                return Object.entries(savedOcrData.lender_nth_selections).map(([lender_id, entry]) => ({
                    lender_id,
                    lender_name: entry.lender_name || 'Lender',
                    selected_values: entry.selected_values || {},
                    percentages: entry.percentages || {},
                }));
            });
        }
    }, [savedOcrData]); // eslint-disable-line react-hooks/exhaustive-deps

    const docDef = documentDefinition || {};
    const maxFiles = docDef.max_files || 5;
    const maxFileSize = (docDef.max_file_size_mb || 5) * 1024 * 1024;
    const allowedFormats = docDef.allowed_formats || ['pdf', 'jpg', 'jpeg', 'png'];
    const canUploadMore = uploadedFiles.length < maxFiles;

    // PrimeReact's basic-mode FileUpload runs its own size/type check but only
    // RENDERS the failure in advanced mode. Without this the file is dropped
    // silently — nothing selected, no error, button looks broken.
    const handleUploadValidationFail = useCallback((file) => {
        const sizeMb = Math.round(maxFileSize / (1024 * 1024));
        const tooBig = file?.size > maxFileSize;
        toast?.current?.show({
            severity: 'warn',
            summary: tooBig ? 'File too large' : 'Unsupported file',
            detail: tooBig
                ? `"${file.name}" is over the ${sizeMb}MB limit. Try a smaller scan or a PDF export.`
                : `"${file?.name || 'That file'}" isn't a supported format. Use ${allowedFormats.map(f => f.toUpperCase()).join(', ')}.`,
            life: 5000,
        });
    }, [toast, maxFileSize, allowedFormats]);

    const handleFileSelect = useCallback(async (event) => {
        const files = event.files || [];
        if (files.length === 0) return;

        const validFiles = [];
        for (const file of files) {
            const validation = validateFile(file);
            if (!validation.valid) {
                toast?.current?.show({ severity: 'error', summary: 'Invalid File', detail: `${file.name}: ${validation.error}`, life: 4000 });
            } else {
                validFiles.push(file);
            }
        }

        if (validFiles.length === 0) { fileUploadRef.current?.clear(); return; }

        // Append-mode: how many existing payslips do we already have OCR for?
        // When the user uploads a 2nd / 3rd payslip after the 1st was already
        // extracted, we want to ADD to the existing results, not wipe them.
        // Cap the total at MAX_PAYSLIPS so a user adding 3 to an existing 1
        // doesn't overflow the comparison table.
        const existingCount = individualResults.length;
        const slotsAvailable = MAX_PAYSLIPS - existingCount;
        if (slotsAvailable <= 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Limit reached',
                detail: `You already have ${MAX_PAYSLIPS} payslips uploaded. Remove one to add another.`,
                life: 4000,
            });
            fileUploadRef.current?.clear();
            return;
        }

        const filesToProcess = validFiles.slice(0, slotsAvailable);
        if (validFiles.length > slotsAvailable) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'File Limit',
                detail: `Only ${slotsAvailable} more payslip${slotsAvailable === 1 ? '' : 's'} can be added (max ${MAX_PAYSLIPS} total). Processing the first ${slotsAvailable}.`,
                life: 4000,
            });
        }

        // Only OCR the NEW files. processFilesWithOCR reads existingIndividualResults
        // and existingOcrResult from state and merges its output on top.
        setSelectedFiles(filesToProcess);
        setOcrError(null);
        // DON'T wipe ocrResult or individualResults — append-mode merges later.
        // Skip the provider-choice dialog: AWS Textract is the default and the only
        // user-facing option now (Sarvam stays available in code as a backup if we
        // ever need to fall back, but we don't ask the user to choose).
        // Called via ref because processFilesWithOCR is declared further down in
        // the component, so a direct identifier reference would be in TDZ at this
        // line during the current render. Same pattern used for onUploadRef etc.
        fileUploadRef.current?.clear();
        processFilesWithOCRRef.current?.(filesToProcess, 'textract');
    }, [toast, individualResults]);

    const processFilesWithOCR = useCallback(async (files, provider = 'textract') => {
        setOcrProcessing(true);
        setOcrProgress(10);
        // Stage 0 — Uploading. File POST in flight to the OCR service. The
        // user spends real wall-clock time here, so the step needs to be
        // shown even though `processPayslip` resolves the whole upload
        // internally before invoking its progress callback.
        setStepperStage(0);

        try {
            let result;
            // Stage 1 — Analysing. OCR engine (Textract / Sarvam) is reading
            // the document. Move into this stage before kicking off the call
            // so the green check on Uploading appears immediately.
            setStepperStage(1);
            // Progress-callback adapter: maps the 0..1 progress from the OCR
            // pipeline into stages 2 (Data Fetching, < 80%) and 3 (Data
            // Formation, ≥ 80%). Without this the indicator would jump
            // straight from 1 to 3 on multi-payslip flows.
            const onPipelineProgress = (progress) => {
                const pct = 30 + Math.round(progress * 0.7);
                setOcrProgress(pct);
                if (pct >= 80) setStepperStage(3);
                else if (pct >= 50) setStepperStage(2);
            };
            if (files.length === 1) {
                setOcrProgress(30);
                result = await processPayslip(files[0], onPipelineProgress, provider, leadId);
            } else {
                setOcrProgress(30);
                result = await processMultiplePayslips(files, onPipelineProgress, provider, leadId);
            }

            setOcrProgress(100);
            setStepperStage(3);

            if (result.success) {
                // For single payslip, individual_results may not exist in API response
                // Build it from the result data so comparison table works consistently
                let individualResultsFromAPI = result.data?.individual_results || result.individual_results || [];
                if (individualResultsFromAPI.length === 0 && files.length === 1 && result.data?.extracted_data) {
                    // Single payslip: create a synthetic individual result
                    individualResultsFromAPI = [{
                        file_name: files[0].name,
                        success: true,
                        extracted_data: result.data.extracted_data,
                        field_mappings: result.data.field_mappings || {},
                        confidence_scores: result.data.confidence_scores || {},
                        processing_time_ms: result.data.processing_time_ms || 0,
                    }];
                }

                // APPEND-MODE MERGE
                // If individualResults already has entries, we just OCR'd a NEW
                // file the user added on top of existing ones (e.g. extracted
                // payslip 1, then uploaded payslip 2 and 3). Merge instead of
                // replace so the comparison table grows from 1 column to 2/3,
                // and the wizard's auto-fill (driven by ocrResult.extracted_data)
                // keeps the FIRST payslip's values — the user has already seen
                // those and may have confirmed them; we don't want a late
                // upload to silently change form fields they've already saved.
                //
                // CRITICAL: read existing values from refs, not from the
                // closure. processFilesWithOCR's useCallback deps don't
                // include individualResults/ocrResult, so the closure is
                // STALE — uploading the 3rd payslip after the 2nd would see
                // individualResults=[] from the initial render and REPLACE
                // instead of merge. Refs always carry the latest committed
                // state.
                const existingIndividualResults = individualResultsRef.current || [];
                const existingOcrResult = ocrResultRef.current;
                const isAppendMode = existingIndividualResults.length > 0;
                const mergedIndividualResults = isAppendMode
                    ? [...existingIndividualResults, ...individualResultsFromAPI]
                    : individualResultsFromAPI;

                if (isAppendMode && existingOcrResult) {
                    setOcrResult({
                        // Preserve the FIRST payslip's extracted_data (the
                        // values the user has already verified/used). New
                        // file's data is in individual_results for the
                        // comparison table; the Default tab stays stable.
                        extracted_data: existingOcrResult.extracted_data,
                        // Same reasoning for field_mappings / confidence_scores
                        // — these drove the wizard's auto-fill on payslip 1.
                        field_mappings: existingOcrResult.field_mappings || {},
                        confidence_scores: existingOcrResult.confidence_scores || {},
                        // Sum processing time across all payslips for honest UX.
                        processing_time_ms: (existingOcrResult.processing_time_ms || 0) + (result.data.processing_time_ms || 0),
                        files_processed: mergedIndividualResults.length,
                    });
                } else {
                    setOcrResult(result.data);
                }
                setIndividualResults(mergedIndividualResults);

                const providerName = provider === 'textract' ? 'AWS Textract' : 'Sarvam AI';
                const fieldsCount = Object.keys(result.data.field_mappings || {}).filter(k => result.data.field_mappings[k]).length;
                const fileInfo = files.length > 1 ? `${result.data.files_processed || files.length} of ${files.length} payslips` : 'payslip';

                toast?.current?.show({ severity: 'success', summary: 'OCR Complete', detail: `Extracted ${fieldsCount} fields from ${fileInfo} in ${result.data.processing_time_ms || 0}ms using ${providerName}`, life: 5000 });

                // If auto-create lead callback is provided, skip OCR dialog and trigger lead creation
                if (onAutoCreateLead) {
                    // Don't show OCR dialog — lead creation first, data shown later
                } else {
                    setShowOcrDialog(true);
                }

                // Persist file data URLs to sessionStorage so payslips are viewable after re-mount
                persistFilesToSession(files);

                // CRITICAL: write OCR JSON to sessionStorage SYNCHRONOUSLY
                // before the upload-handler fires below. The downstream upload
                // pipeline (useDocumentUpload's handleDocumentSelect → flush)
                // reads OCR JSON from sessionStorage to ship it with the
                // /documents/upload call, so the backend can save it to
                // lead.ocr_payslip_details in one shot. If we waited for the
                // React state cycle (onFullOCRDataRef.current → setState →
                // useEffect → sessionStorage), there's a race: the upload
                // handler runs FIRST (line below), reads empty sessionStorage,
                // and queues the file without OCR. Result: 3/8 fresh leads
                // had PDF in S3 but no ocr_payslip_details column.
                try {
                    const earlyOcrPayload = {
                        extracted_data: result.data.extracted_data,
                        field_mappings: result.data.field_mappings,
                        confidence_scores: result.data.confidence_scores,
                        individual_results: individualResultsFromAPI,
                        processing_time_ms: result.data.processing_time_ms || 0,
                        files_processed: result.data.files_processed || 1,
                        extracted_at: new Date().toISOString(),
                    };
                    sessionStorage.setItem('ocr_payslip_data', JSON.stringify(earlyOcrPayload));
                } catch (e) {
                    console.warn('PayslipOCRUpload: failed to pre-write OCR JSON to sessionStorage', e);
                }

                if (onUploadRef.current) { for (const file of files) await onUploadRef.current(file, docDef.id); }

                // Send full OCR data via dedicated callback (for saving/restoring).
                // Use ref to get latest callback (avoids stale closure after long async OCR).
                // In append-mode we send the MERGED set so the parent persists
                // all N payslips, not just the latest upload. Without this, the
                // backend save would overwrite ocr_payslip_details.individual_results
                // with the 3rd payslip alone, wiping payslips 1 and 2 from the DB
                // even though they're still visible in the comparison table.
                if (onFullOCRDataRef.current) {
                    const fullData = isAppendMode && existingOcrResult
                        ? {
                            extracted_data: existingOcrResult.extracted_data,
                            field_mappings: existingOcrResult.field_mappings || {},
                            confidence_scores: existingOcrResult.confidence_scores || {},
                            individual_results: mergedIndividualResults,
                            processing_time_ms: (existingOcrResult.processing_time_ms || 0) + (result.data.processing_time_ms || 0),
                            files_processed: mergedIndividualResults.length,
                        }
                        : {
                            extracted_data: result.data.extracted_data,
                            field_mappings: result.data.field_mappings,
                            confidence_scores: result.data.confidence_scores,
                            individual_results: individualResultsFromAPI,
                            processing_time_ms: result.data.processing_time_ms || 0,
                            files_processed: result.data.files_processed || 1,
                        };
                    onFullOCRDataRef.current(fullData);
                }

                // Fire onOCRComplete so identifier fields (and the full
                // payload) flow into formData immediately. The user needs to
                // see at least the full_name in the Borrower section as
                // confirmation that the upload worked. The fourth argument
                // ('auto') tells the parent this fire is the auto-extraction
                // event, NOT the user clicking "Use Values" — the parent
                // should NOT close its drawer or trigger offer-gen yet.
                if (onOCRCompleteRef.current) {
                    onOCRCompleteRef.current(
                        result.data.field_mappings,
                        result.data.extracted_data,
                        result.data.confidence_scores,
                        { source: 'auto' }
                    );
                }

                // Auto-create lead in the background using only the identifier
                // fields. Runs in parallel with form pre-fill above. The delay
                // gives React a chance to commit the formData update so the
                // duplicate-check (phone/PAN) inside createLead can find the
                // values via findFieldValue() — and the OCR payload is passed
                // as a fallback too for safety.
                if (onAutoCreateLead) {
                    const extracted = result.data.extracted_data || {};
                    const mappings = result.data.field_mappings || {};
                    setTimeout(() => onAutoCreateLead({
                        full_name: mappings.employee_name || extracted.employee_name || null,
                        phone: mappings.mobile_number || extracted.mobile_number || null,
                        pan: mappings.pan_number || extracted.pan_number || null,
                        email: mappings.email || extracted.email || null,
                    }), 800);
                }
            } else {
                throw new Error(result.error || 'OCR processing failed');
            }
        } catch (error) {
            console.error('OCR Error:', error);
            setOcrError(error.response?.data?.message || error.message || 'OCR processing failed');
            toast?.current?.show({ severity: 'error', summary: 'OCR Failed', detail: error.response?.data?.message || error.message || 'Failed to extract data.', life: 5000 });
            if (onUploadRef.current) { for (const file of files) await onUploadRef.current(file, docDef.id); }
        } finally {
            // Two-phase teardown so the user sees stage 4 (Data Formation)
            // light up green before the indicator disappears:
            //   1. Bump stage to 4 (out-of-range = "all done") so every
            //      step renders with the green check.
            //   2. After a brief hold, unmount the indicator.
            setTimeout(() => setStepperStage(4), 250);
            setTimeout(() => {
                setOcrProcessing(false);
                setOcrProgress(0);
                setStepperStage(null);
            }, 900);
        }
    }, [docDef.id, toast, persistFilesToSession, leadId]);

    // Mirror processFilesWithOCR to its forward ref so handleFileSelect (earlier
    // in render order) can invoke the latest stable callback without TDZ.
    useEffect(() => {
        processFilesWithOCRRef.current = processFilesWithOCR;
    }, [processFilesWithOCR]);

    /**
     * Build comparison table rows for multiple payslips.
     * Each row: { label, field, isCurrency, category, val_0, val_1, val_2, hasVariance }
     * No average column — replaced with verification column.
     */
    const comparisonData = useMemo(() => {
        const successResults = individualResults.filter(r => r.success);
        const hasMultiple = successResults.length > 1;

        if (!hasMultiple && !ocrResult?.extracted_data) return [];

        const mergedData = ocrResult?.extracted_data || {};
        const rows = [];

        // Variance projection — shared between fixed-key rows and dynamic
        // rows. Walks each payslip's data, pulls the requested value via
        // `pickValue(payslipData)`, fills row.val_N / row.raw_N / sets
        // row.hasVariance + row.varianceMsg.
        const projectVariance = (row, pickValue, isCurrency) => {
            let hasValue = false;
            if (hasMultiple) {
                const rawValues = [];
                successResults.forEach((result, idx) => {
                    const val = pickValue(result.extracted_data || {});
                    if (val !== null && val !== undefined && val !== '' && val !== 0) {
                        hasValue = true;
                        row[`val_${idx}`] = isCurrency && typeof val === 'number' ? formatCurrency(val) : val;
                        row[`raw_${idx}`] = val;
                        rawValues.push(val);
                    } else {
                        row[`val_${idx}`] = '-';
                        row[`raw_${idx}`] = null;
                        rawValues.push(null);
                    }
                });
                const nonNullValues = rawValues.filter(v => v !== null);
                if (nonNullValues.length > 0) {
                    if (typeof nonNullValues[0] === 'number') {
                        row.hasVariance = !nonNullValues.every(v => v === nonNullValues[0]);
                        if (row.hasVariance) {
                            const min = Math.min(...nonNullValues);
                            const max = Math.max(...nonNullValues);
                            const diff = ((max - min) / min * 100).toFixed(0);
                            row.varianceMsg = `Varies ${diff}%`;
                        }
                    } else {
                        const normalized = nonNullValues.map(v => String(v).toLowerCase().trim());
                        row.hasVariance = !normalized.every(v => v === normalized[0]);
                        if (row.hasVariance) row.varianceMsg = 'Values differ';
                    }
                    if (rawValues.some(v => v === null) && nonNullValues.length > 0) {
                        row.hasVariance = true;
                        row.varianceMsg = row.varianceMsg || 'Missing in some';
                    }
                }
            } else {
                const val = pickValue(mergedData);
                if (val !== null && val !== undefined && val !== '' && val !== 0) {
                    hasValue = true;
                    row.val_0 = isCurrency && typeof val === 'number' ? formatCurrency(val) : val;
                    row.raw_0 = val;
                } else {
                    row.val_0 = '-';
                    row.raw_0 = null;
                }
            }
            return hasValue;
        };

        // Locate the Earnings + Deductions group indices upfront so the
        // dynamic rows inserted from extracted_data.earnings / .deductions
        // land in the correct category buckets used by the UI panel split.
        const earningsGroupIdx = FIELD_GROUPS.findIndex(g => g.label === 'Earnings');
        const deductionsGroupIdx = FIELD_GROUPS.findIndex(g => g.label === 'Deductions');

        // Dynamic mode: prefer the per-payslip `earnings` / `deductions`
        // arrays returned by the LLM. Each row preserves the EXACT payslip
        // label (e.g. "House Rent Allowance", "Education Allowance") and
        // payslip order. Falls back to FIELD_GROUPS for any section the
        // arrays don't cover (or when the arrays are missing entirely).
        const dynamicEarnings = Array.isArray(mergedData?.earnings) ? mergedData.earnings : [];
        const dynamicDeductions = Array.isArray(mergedData?.deductions) ? mergedData.deductions : [];

        const buildDynamicRows = (items, category, groupIdx, kind) => {
            // De-dup by slug while preserving payslip order. Two rows with the
            // same slug (e.g. duplicate "Conveyance" entries) merge into one.
            const seen = new Set();
            items.forEach(it => {
                const label = String(it?.label || '').trim();
                if (!label) return;
                const slug = slugifyLabel(label);
                if (!slug || seen.has(slug)) return;
                seen.add(slug);

                const row = {
                    field: slug,
                    label,
                    category,
                    categoryOrder: groupIdx,
                    isCurrency: true,
                    hasVariance: false,
                    kind, // 'earning' | 'deduction' — used by computeNthFromSelections
                };

                const pickValue = (payslip) => {
                    // Try the dynamic array on THIS payslip first (label match,
                    // case-insensitive). If absent, fall back to a flat-key
                    // lookup so legacy GPT responses still surface a value.
                    const list = Array.isArray(payslip?.[kind === 'earning' ? 'earnings' : 'deductions'])
                        ? payslip[kind === 'earning' ? 'earnings' : 'deductions']
                        : [];
                    const hit = list.find(x => slugifyLabel(x?.label) === slug);
                    if (hit && typeof hit.amount === 'number') return hit.amount;
                    return payslip?.[slug];
                };

                if (projectVariance(row, pickValue, true)) rows.push(row);
            });
        };

        if (dynamicEarnings.length > 0 && earningsGroupIdx !== -1) {
            buildDynamicRows(dynamicEarnings, 'Earnings', earningsGroupIdx, 'earning');
        }
        if (dynamicDeductions.length > 0 && deductionsGroupIdx !== -1) {
            buildDynamicRows(dynamicDeductions, 'Deductions', deductionsGroupIdx, 'deduction');
        }

        // Legacy + non-dynamic groups (Identity, Pay Period, Net Pay, ...)
        // — and Earnings / Deductions if the LLM didn't return arrays (safety
        // net). When dynamic rows already populated a category, the fixed-key
        // loop for that category is skipped to avoid duplicates.
        FIELD_GROUPS.forEach((group, groupIdx) => {
            if (group.label === 'Earnings' && dynamicEarnings.length > 0) return;
            if (group.label === 'Deductions' && dynamicDeductions.length > 0) return;
            const isCurrency = group.currency || false;
            group.fields.forEach(({ key }) => {
                const row = {
                    field: key,
                    label: getFieldLabel(key),
                    category: group.label,
                    categoryOrder: groupIdx,
                    isCurrency,
                    hasVariance: false,
                    kind: group.label === 'Earnings' ? 'earning'
                        : group.label === 'Deductions' ? 'deduction'
                        : undefined,
                };
                if (projectVariance(row, (payslip) => payslip?.[key], isCurrency)) {
                    rows.push(row);
                }
            });
        });

        return rows;
    }, [individualResults, ocrResult]);

    // Auto-verify rows where all payslip values are consistent (no variance)
    useEffect(() => {
        if (comparisonData.length === 0) return;
        const successResults = individualResults.filter(r => r.success);
        if (successResults.length < 2) return;

        setVerificationStatus(prev => {
            const updated = { ...prev };
            let changed = false;
            comparisonData.forEach(row => {
                if (!row.hasVariance && !prev[row.field]) {
                    // Consistent row not yet manually touched — auto-verify
                    updated[row.field] = { status: 'verified', queryText: '', auto: true };
                    changed = true;
                }
            });
            if (changed) {
                // Persist auto-verification to sessionStorage
                try {
                    const cached = sessionStorage.getItem(OCR_DATA_KEY);
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        parsed.verification_status = updated;
                        sessionStorage.setItem(OCR_DATA_KEY, JSON.stringify(parsed));
                    }
                } catch {}
                setVerificationVersion(v => v + 1);
                return updated;
            }
            return prev;
        });
    }, [comparisonData, individualResults]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Build variance-based analytics from multiple payslips.
     * Detects: field-level variances, missing months, anomalies.
     */
    const analyticsData = useMemo(() => {
        const successResults = individualResults.filter(r => r.success);
        if (successResults.length < 2) return null;

        // Parse pay periods to detect missing months
        const payPeriods = successResults.map(r => r.extracted_data?.pay_period).filter(Boolean);
        const parsedMonths = payPeriods.map(p => {
            // Try parsing "Jan/2026", "January 2026", "01/2026", etc.
            const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const str = String(p).toLowerCase().trim();
            let month = -1, year = -1;
            // Try "Mon/YYYY" or "Mon-YYYY" or "Mon YYYY"
            for (let i = 0; i < monthNames.length; i++) {
                if (str.startsWith(monthNames[i])) {
                    month = i;
                    const yearMatch = str.match(/(\d{4})/);
                    if (yearMatch) year = parseInt(yearMatch[1]);
                    break;
                }
            }
            // Try "MM/YYYY"
            if (month === -1) {
                const numMatch = str.match(/^(\d{1,2})[\/\-](\d{4})$/);
                if (numMatch) {
                    month = parseInt(numMatch[1]) - 1;
                    year = parseInt(numMatch[2]);
                }
            }
            return month >= 0 && year > 0 ? { month, year, label: p } : null;
        }).filter(Boolean);

        // Detect missing months (gaps in sequence)
        const missingMonths = [];
        if (parsedMonths.length >= 2) {
            // Sort by date
            const sorted = [...parsedMonths].sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month));
            const newest = sorted[sorted.length - 1];
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            // Check for gaps between uploaded payslips
            for (let i = 0; i < sorted.length - 1; i++) {
                const curr = sorted[i].year * 12 + sorted[i].month;
                const next = sorted[i + 1].year * 12 + sorted[i + 1].month;
                for (let m = curr + 1; m < next; m++) {
                    const gapMonth = m % 12;
                    const gapYear = Math.floor(m / 12);
                    missingMonths.push(`${monthNames[gapMonth]}/${gapYear}`);
                }
            }

            // Check if current month is missing (compare with today)
            const now = new Date();
            const currentMonthVal = now.getFullYear() * 12 + now.getMonth();
            const newestVal = newest.year * 12 + newest.month;
            // If latest payslip is older than current month, flag it
            if (currentMonthVal > newestVal) {
                for (let m = newestVal + 1; m <= currentMonthVal; m++) {
                    const gapMonth = m % 12;
                    const gapYear = Math.floor(m / 12);
                    missingMonths.push(`${monthNames[gapMonth]}/${gapYear}`);
                }
            }
        }

        // Collect field-level variance details
        const variances = [];
        const getValues = (key) => successResults.map((r, idx) => ({
            val: r.extracted_data?.[key],
            period: r.extracted_data?.pay_period || `Payslip ${idx + 1}`
        }));

        // Check key salary fields for variances
        const keyFields = [
            { key: 'net_salary', label: 'Net Salary', alt: 'net_pay' },
            { key: 'gross_salary', label: 'Gross Salary', alt: 'total_earnings' },
            { key: 'basic_salary', label: 'Basic Salary' },
            { key: 'special_allowance', label: 'Special Allowance' },
            { key: 'hra', label: 'HRA' },
            { key: 'total_deductions', label: 'Total Deductions' },
            { key: 'incentive', label: 'Incentive' },
            { key: 'bonus', label: 'Bonus' },
            { key: 'overtime_allowance', label: 'Overtime' },
            { key: 'arrears', label: 'Arrears' },
        ];

        keyFields.forEach(({ key, label, alt }) => {
            let entries = getValues(key);
            // Fallback to alt key if primary has no values
            if (alt && entries.every(e => !e.val || e.val === 0)) {
                entries = getValues(alt);
            }
            const numericEntries = entries.filter(e => typeof e.val === 'number' && e.val > 0);
            const zeroOrMissing = entries.filter(e => !e.val || e.val === 0);

            if (numericEntries.length === 0) return;

            // Check for values present in some months but missing in others
            if (zeroOrMissing.length > 0 && numericEntries.length > 0) {
                const presentIn = numericEntries.map(e => e.period).join(', ');
                const missingIn = zeroOrMissing.map(e => e.period).join(', ');
                variances.push({
                    field: label,
                    type: 'missing',
                    message: `Present in ${presentIn} but missing in ${missingIn}`,
                    severity: 'warning',
                });
            }

            // Check for numeric differences
            if (numericEntries.length >= 2) {
                const vals = numericEntries.map(e => e.val);
                const min = Math.min(...vals);
                const max = Math.max(...vals);
                if (min !== max) {
                    const diffPercent = ((max - min) / min * 100).toFixed(1);
                    const highest = numericEntries.find(e => e.val === max);
                    const lowest = numericEntries.find(e => e.val === min);
                    variances.push({
                        field: label,
                        type: 'varies',
                        message: `Varies by ${diffPercent}% — ${formatCurrency(min)} (${lowest.period}) to ${formatCurrency(max)} (${highest.period})`,
                        severity: parseFloat(diffPercent) > 20 ? 'error' : 'warning',
                    });
                }
            }
        });

        // Count consistent fields (no variance)
        const totalVarianceRows = comparisonData.filter(r => r.hasVariance).length;
        const totalRows = comparisonData.length;
        const consistentRows = totalRows - totalVarianceRows;

        return {
            payPeriods,
            missingMonths,
            variances,
            totalRows,
            consistentRows,
            totalVarianceRows,
        };
    }, [individualResults, comparisonData]);

    // Auto-select the latest month (first column, index 0) by default for all rows.
    // Auto-calc rows (Gross / Net / Total Deductions) are excluded because their value is
    // derived from selected line items — keeping them out of selectedValues prevents the
    // calc memo from double-counting and matches the non-selectable UI from Edit 1.
    useEffect(() => {
        if (comparisonData.length > 0 && Object.keys(selectedValues).length === 0) {
            const autoSelected = {};
            comparisonData.forEach(row => {
                if (AUTO_CALC_FIELDS.includes(row.field)) return;
                if (row.raw_0 !== null && row.raw_0 !== undefined) {
                    autoSelected[row.field] = 0;
                }
            });
            if (Object.keys(autoSelected).length > 0) {
                setSelectedValues(autoSelected);
            }
        }
    }, [comparisonData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Per-payslip-column totals — for each payslip index (0/1/2), sum that payslip's own
    // earning rows and deduction rows from comparisonData. Used to overwrite the auto-calc
    // rows' per-column display so each Payslip column shows its OWN computed totals (not
    // OCR-reported values, which may disagree with the sum of line items on that slip).
    const perColumnTotals = useMemo(() => {
        const okResults = individualResults.filter(r => r.success !== false);
        const columnCount = okResults.length > 1 ? okResults.length : 1;
        const columnIndices = Array.from({ length: columnCount }, (_, i) => i);
        const totalsByColumn = {};
        columnIndices.forEach(idx => {
            let earn = 0;
            let ded = 0;
            comparisonData.forEach(row => {
                if (AUTO_CALC_FIELDS.includes(row.field)) return;
                if (!row.isCurrency) return;
                const v = row[`raw_${idx}`];
                if (typeof v !== 'number') return;
                const kind = row.kind
                    || (EARNING_FIELDS.includes(row.field) ? 'earning'
                        : DEDUCTION_FIELDS.includes(row.field) ? 'deduction'
                        : null);
                if (kind === 'earning') earn += v;
                else if (kind === 'deduction') ded += v;
            });
            totalsByColumn[idx] = {
                total_earnings: earn,
                gross_salary: earn,
                total_deductions: ded,
                net_pay: earn - ded,
                net_salary: earn - ded,
                take_home: earn - ded,
            };
        });
        return totalsByColumn;
    }, [comparisonData, individualResults]);

    // Calculated values derived from the user's row selections. For each non-auto-calc row that
    // the user has selected, include that payslip's value (× percentage). Auto-calc rows
    // (gross / net / total deductions / net salary / take home) are then computed from the
    // included line items so they always reflect the live state of the selection toggles.
    const calculatedValues = useMemo(() => {
        const result = {};
        comparisonData.forEach(row => {
            if (AUTO_CALC_FIELDS.includes(row.field)) return;

            const selIdx = selectedValues[row.field];
            if (selIdx === undefined || selIdx === null) return;
            const rawVal = row[`raw_${selIdx}`];
            if (rawVal === null || rawVal === undefined) return;

            if (row.isCurrency && typeof rawVal === 'number') {
                const pct = percentages[row.field] !== undefined ? percentages[row.field] : 100;
                result[row.field] = Math.round(rawVal * pct / 100);
            } else {
                result[row.field] = rawVal;
            }
        });

        // Sum from comparisonData row.kind so dynamic slugs (e.g. 'house_rent_allowance')
        // are counted alongside the legacy fixed keys. Falls back to the
        // allowlists for rows lacking a kind (older OCR data already in DB).
        let totalEarnings = 0;
        let totalDeductions = 0;
        comparisonData.forEach(row => {
            if (AUTO_CALC_FIELDS.includes(row.field)) return;
            const amt = result[row.field];
            if (typeof amt !== 'number') return;
            const kind = row.kind
                || (EARNING_FIELDS.includes(row.field) ? 'earning'
                    : DEDUCTION_FIELDS.includes(row.field) ? 'deduction'
                    : null);
            if (kind === 'earning') totalEarnings += amt;
            else if (kind === 'deduction') totalDeductions += amt;
        });
        const netPay = totalEarnings - totalDeductions;

        if (totalEarnings > 0) {
            result.gross_salary = totalEarnings;
            result.total_earnings = totalEarnings;
        }
        if (totalDeductions > 0) {
            result.total_deductions = totalDeductions;
        }
        if (totalEarnings > 0 || totalDeductions > 0) {
            result.net_salary = netPay;
            result.net_pay = netPay;
            result.take_home = netPay;
        }

        return result;
    }, [comparisonData, selectedValues, percentages]);

    // Embed selection/percentage/calculated state into each row so PrimeReact DataTable
    // detects data changes and re-renders body templates (it caches internally with rowGroupMode).
    // For auto-calc rows: (a) overwrite each Payslip column to show that payslip's own computed
    // total instead of OCR-reported value, and (b) overwrite _calcVal with the live composite
    // from calculatedValues so the Calculated cell reflects selection toggles immediately.
    const tableData = useMemo(() => comparisonData.map(row => {
        const selIdx = selectedValues[row.field] !== undefined ? selectedValues[row.field] : -1;
        const pct = percentages[row.field] !== undefined ? percentages[row.field] : 100;
        let calcVal = null;
        if (selIdx >= 0) {
            const rawVal = row[`raw_${selIdx}`];
            if (rawVal !== null && rawVal !== undefined) {
                calcVal = (row.isCurrency && typeof rawVal === 'number') ? Math.round(rawVal * pct / 100) : rawVal;
            }
        }

        const overrides = {};
        if (AUTO_CALC_FIELDS.includes(row.field) && row.isCurrency) {
            Object.keys(perColumnTotals).forEach(idx => {
                const total = perColumnTotals[idx]?.[row.field];
                if (typeof total === 'number') {
                    overrides[`raw_${idx}`] = total;
                    overrides[`val_${idx}`] = total > 0 ? formatCurrency(total) : '-';
                }
            });
            const liveCalc = calculatedValues[row.field];
            if (typeof liveCalc === 'number') {
                calcVal = liveCalc;
            }
        }

        return { ...row, ...overrides, _selIdx: selIdx, _pct: pct, _calcVal: calcVal };
    }), [comparisonData, selectedValues, percentages, perColumnTotals, calculatedValues]);

    // Split tableData into two presentation buckets:
    //  - calcRows: salary calculation lines (Earnings, Deductions, Net Pay) shown in a sticky
    //    DataTable at the top of the dialog so the operator never loses the math while scrolling.
    //  - identityRows: company/employee/identity/bank/pay-period fields shown ONCE in a flat
    //    key-value grid below. These don't vary across payslips so per-column comparison is noise.
    const calcRows = useMemo(() => tableData.filter(r => CALC_CATEGORIES.has(r.category)), [tableData]);
    const identityRows = useMemo(() => tableData.filter(r => !CALC_CATEGORIES.has(r.category)), [tableData]);

    /**
     * Handle verification status change for a row
     */
    const handleVerification = useCallback((fieldKey, newStatus) => {
        setVerificationStatus(prev => {
            const current = prev[fieldKey] || {};
            let updated;
            if (current.status === newStatus) {
                // Toggle off if clicking same status
                updated = { ...prev };
                delete updated[fieldKey];
            } else {
                updated = {
                    ...prev,
                    [fieldKey]: { ...current, status: newStatus, queryText: current.queryText || '' }
                };
            }
            // Immediately persist to sessionStorage so data survives refresh
            try {
                const cached = sessionStorage.getItem(OCR_DATA_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    parsed.verification_status = updated;
                    sessionStorage.setItem(OCR_DATA_KEY, JSON.stringify(parsed));
                }
            } catch {}
            return updated;
        });
        setVerificationVersion(v => v + 1);
    }, []);

    const handleQueryTextChange = useCallback((fieldKey, text) => {
        setVerificationStatus(prev => {
            const updated = {
                ...prev,
                [fieldKey]: { ...prev[fieldKey], status: 'query', queryText: text }
            };
            // Immediately persist to sessionStorage
            try {
                const cached = sessionStorage.getItem(OCR_DATA_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    parsed.verification_status = updated;
                    sessionStorage.setItem(OCR_DATA_KEY, JSON.stringify(parsed));
                }
            } catch {}
            return updated;
        });
    }, []);

    /**
     * Save verification data — persists verification status via onFullOCRData.
     * Called by "Save Verification" button and on dialog close.
     */
    const [verificationSaved, setVerificationSaved] = useState(false);

    // Build the Default-tab snapshot used for "Use Values" and "Save
    // Verification". When user is on a lender tab, live state belongs to that
    // lender — we must use the persisted Default snapshot for the wizard's
    // form fields (Net Salary, Gross, etc.). Per-lender values get their own
    // bucket inside lender_nth_selections.
    const buildDefaultSnapshot = useCallback(() => {
        const defaultSel = activeTabIndex === 0
            ? selectedValues
            : (defaultTabSelectionsRef.current.selectedValues || {});
        const defaultPct = activeTabIndex === 0
            ? percentages
            : (defaultTabSelectionsRef.current.percentages || {});

        const result = {};
        // Kind-aware totals: use row.kind first (dynamic rows from the LLM's
        // earnings[]/deductions[] arrays carry their own classification) and
        // fall back to the legacy EARNING_FIELDS/DEDUCTION_FIELDS allowlists
        // only when kind is absent. The previous implementation summed via
        // the static allowlists only, which silently dropped any payslip row
        // whose slug wasn't in the lists (e.g. "PERSONAL PAY" → personal_pay
        // was missing from EARNING_FIELDS, so Net Pay came out as Basic + HRA
        // only). Matches the logic in computeNthFromSelections() exactly.
        const earningsTotal = { v: 0 };
        const deductionsTotal = { v: 0 };
        comparisonData.forEach(row => {
            if (AUTO_CALC_FIELDS.includes(row.field)) return;
            const selIdx = defaultSel?.[row.field];
            if (selIdx === undefined || selIdx === null) return;
            const rawVal = row[`raw_${selIdx}`];
            if (rawVal === null || rawVal === undefined) return;

            let written;
            if (row.isCurrency && typeof rawVal === 'number') {
                const pct = defaultPct?.[row.field] !== undefined ? defaultPct[row.field] : 100;
                written = Math.round(rawVal * pct / 100);
            } else {
                written = rawVal;
            }
            result[row.field] = written;

            if (typeof written !== 'number') return;
            const kind = row.kind
                || (EARNING_FIELDS.includes(row.field) ? 'earning'
                    : DEDUCTION_FIELDS.includes(row.field) ? 'deduction'
                    : null);
            if (kind === 'earning') earningsTotal.v += written;
            else if (kind === 'deduction') deductionsTotal.v += written;
        });
        const totalEarnings = earningsTotal.v;
        const totalDeductions = deductionsTotal.v;
        const netPay = Math.max(0, totalEarnings - totalDeductions);
        if (totalEarnings > 0) { result.gross_salary = totalEarnings; result.total_earnings = totalEarnings; }
        if (totalDeductions > 0) { result.total_deductions = totalDeductions; }
        if (totalEarnings > 0 || totalDeductions > 0) {
            result.net_salary = netPay;
            result.net_pay = netPay;
            result.take_home = netPay;
        }
        return { defaultSel, defaultPct, defaultCalculated: result };
    }, [activeTabIndex, selectedValues, percentages, comparisonData]);

    const saveVerificationData = useCallback(() => {
        if (onFullOCRDataRef.current && ocrResult) {
            const { defaultSel, defaultPct, defaultCalculated } = buildDefaultSnapshot();

            // Build the per-lender NTH selections map for ALL configured lender tabs.
            // For the currently active lender tab (if any), use the live selectedValues +
            // percentages state since they haven't been written back to lenderTabs yet.
            const lenderNthSelections = {};
            lenderTabs.forEach((tab, idx) => {
                const liveActive = (activeTabIndex - 1) === idx;
                const sel = liveActive ? selectedValues : (tab.selected_values || {});
                const pct = liveActive ? percentages : (tab.percentages || {});
                lenderNthSelections[tab.lender_id] = {
                    lender_name: tab.lender_name,
                    selected_values: sel,
                    percentages: pct,
                    nth_value: computeNthFromSelections(comparisonData, sel, pct),
                    saved_at: new Date().toISOString(),
                };
            });

            const fullData = {
                extracted_data: ocrResult.extracted_data,
                field_mappings: ocrResult.field_mappings,
                confidence_scores: ocrResult.confidence_scores,
                individual_results: individualResults,
                processing_time_ms: ocrResult.processing_time_ms || 0,
                files_processed: ocrResult.files_processed || 1,
                verification_status: verificationStatus,
                selected_values: defaultSel,
                percentages: defaultPct,
                calculated_values: defaultCalculated,
                lender_nth_selections: lenderNthSelections,
            };
            onFullOCRDataRef.current(fullData);
            setVerificationSaved(true);
            if (toast?.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Verification Saved',
                    detail: 'Your verification and queries have been saved successfully.',
                    life: 3000,
                });
            }
            // Reset saved indicator after 2 seconds
            setTimeout(() => setVerificationSaved(false), 2000);
        }
    }, [ocrResult, individualResults, verificationStatus, selectedValues, percentages, calculatedValues, comparisonData, lenderTabs, activeTabIndex, buildDefaultSnapshot, toast]);

    const handleDialogClose = useCallback(() => {
        setShowOcrDialog(false);
        // Auto-save on close as well
        saveVerificationData();
    }, [saveVerificationData]);

    const renderAnalytics = () => {
        if (!analyticsData) return null;

        return (
            // This block now leads the panel, so the Divider that used to separate
            // it from the content above is gone and the clearance moved below it.
            <div className="mb-3">
                <div className="flex flex-column md:flex-row md:align-items-center gap-2 mb-2">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-search text-primary text-sm" />
                        <span className="font-bold text-sm text-primary">Payslip Analysis</span>
                    </div>
                    {analyticsData.payPeriods.length > 0 && (
                        // Pay periods stack one-below-one on mobile, sit inline on
                        // desktop. Each is a subtle chip so the distinct payslip
                        // months read at a glance.
                        <div className="flex flex-column md:flex-row md:flex-wrap gap-1 md:gap-2">
                            {analyticsData.payPeriods.map((period, i) => (
                                <span
                                    key={`pp-${i}`}
                                    className="text-xs text-600 surface-100 border-round px-2 py-1 align-self-start"
                                >
                                    {period}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Verification Summary — icon badge left, label over value, status
                    chip right. Chips are derived from real state, never decorative:
                    each only appears when it carries information. */}
                {(() => {
                    const verifiedCount = Object.values(verificationStatus).filter(v => v.status === 'verified').length;
                    const queryCount = Object.values(verificationStatus).filter(v => v.status === 'query').length;
                    const totalRows = analyticsData.totalRows || 0;
                    const consistentPct = totalRows ? Math.round((analyticsData.consistentRows / totalRows) * 100) : 0;
                    const verifiedPct = totalRows ? Math.round((verifiedCount / totalRows) * 100) : 0;

                    const cards = [
                        {
                            tone: 'green',
                            icon: 'pi-check-circle',
                            label: 'Consistent',
                            value: analyticsData.consistentRows,
                            chip: totalRows ? `${consistentPct}%` : null,
                            chipIcon: 'pi-arrow-up',
                            foot: 'fields match across payslips',
                        },
                        {
                            tone: 'orange',
                            icon: 'pi-exclamation-triangle',
                            label: 'Varies',
                            value: analyticsData.totalVarianceRows,
                            chip: analyticsData.totalVarianceRows > 0 ? 'Review' : 'Clear',
                            chipIcon: analyticsData.totalVarianceRows > 0 ? 'pi-arrow-down' : 'pi-check',
                            foot: analyticsData.totalVarianceRows > 0 ? 'need a closer look' : 'no differences found',
                        },
                        {
                            tone: 'blue',
                            icon: 'pi-verified',
                            label: 'Verified',
                            value: `${verifiedCount}/${totalRows}`,
                            chip: totalRows ? `${verifiedPct}%` : null,
                            chipIcon: verifiedCount === totalRows && totalRows > 0 ? 'pi-check' : 'pi-clock',
                            foot: verifiedCount === totalRows && totalRows > 0 ? 'all fields confirmed' : 'still to confirm',
                        },
                        {
                            tone: 'yellow',
                            icon: 'pi-flag-fill',
                            label: 'Queries',
                            value: queryCount,
                            chip: queryCount > 0 ? 'Open' : 'None',
                            chipIcon: queryCount > 0 ? 'pi-exclamation-circle' : 'pi-check',
                            foot: queryCount > 0 ? 'raised for the borrower' : 'nothing flagged',
                        },
                    ];

                    return (
                        <div className="grid mb-2">
                            {cards.map((c) => (
                                <div key={c.label} className="col-12 md:col-6 lg:col-3">
                                    {/* Icon on the left, label + value stacked beside it,
                                        chip pinned right — one row instead of three, which
                                        roughly halves the card height. */}
                                    <div className={`ps-stat ps-stat--${c.tone} border-round-lg p-2 h-full flex align-items-center gap-2`}>
                                        <span className="ps-stat__icon flex align-items-center justify-content-center border-round flex-shrink-0" aria-hidden="true">
                                            <i className={`pi ${c.icon}`} />
                                        </span>
                                        <div className="flex flex-column min-w-0">
                                            <span className="ps-stat__label text-xs">{c.label}</span>
                                            <span className="ps-stat__value font-bold">{c.value}</span>
                                        </div>
                                        {c.chip && (
                                            <span className="ps-stat__chip text-xs font-semibold border-round flex align-items-center gap-1 flex-shrink-0 ml-auto">
                                                <i className={`pi ${c.chipIcon}`} style={{ fontSize: '.6rem' }} />
                                                {c.chip}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {/* Missing Months Alert */}
                {analyticsData.missingMonths.length > 0 && (
                    <Message
                        severity="warn"
                        className="w-full mb-2"
                        content={
                            <div className="flex align-items-center gap-2 text-xs">
                                <i className="pi pi-exclamation-triangle" />
                                <span>
                                    <strong>Missing payslip{analyticsData.missingMonths.length > 1 ? 's' : ''}:</strong>{' '}
                                    {analyticsData.missingMonths.join(', ')} — why {analyticsData.missingMonths.length > 1 ? 'were these' : 'was this'} not uploaded?
                                </span>
                            </div>
                        }
                    />
                )}

                {/* Field-Level Variance Details — each row is a card with a circular
                    icon badge, so the list reads as discrete findings rather than a
                    run of tinted strips. */}
                {analyticsData.variances.length > 0 && (
                    <div className="ps-variance mt-2 p-3 border-round-lg hidden md:block">
                        <div className="flex align-items-center gap-2 mb-2">
                            <i className="pi pi-info-circle text-primary" aria-hidden="true" />
                            <span className="font-semibold text-sm text-primary">Variance Details</span>
                            <span className="ps-variance__count text-xs font-semibold border-round ml-auto">
                                {analyticsData.variances.length}
                            </span>
                        </div>

                        <div className="flex flex-column gap-2">
                            {analyticsData.variances.map((v, idx) => (
                                <div
                                    key={idx}
                                    className={`ps-variance__row ${v.severity === 'error' ? 'is-error' : ''} flex align-items-center gap-2 p-2 border-round-lg`}
                                >
                                    <span className="ps-variance__badge flex align-items-center justify-content-center border-circle flex-shrink-0" aria-hidden="true">
                                        <i className={`pi ${v.type === 'missing' ? 'pi-minus-circle' : 'pi-arrows-h'}`} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="ps-variance__field text-sm font-semibold">{v.field}</div>
                                        <div className="text-xs text-600 line-height-2">{v.message}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Queries Summary */}
                {Object.values(verificationStatus).some(v => v.status === 'query') && (
                    <div className="mt-2">
                        <div className="flex align-items-center gap-1 mb-1">
                            <i className="pi pi-comment text-blue-500 text-xs" />
                            <span className="font-semibold text-xs text-blue-700">Queries Raised</span>
                        </div>
                        {Object.entries(verificationStatus).filter(([, v]) => v.status === 'query').map(([fieldKey, v]) => (
                            <div key={fieldKey} className="flex align-items-start gap-2 p-1 mb-1 border-round text-xs bg-blue-50 border-1 border-blue-200">
                                <i className="pi pi-flag mt-1 text-blue-500 text-xs" />
                                <div>
                                    <span className="font-semibold">{getFieldLabel(fieldKey)}:</span>{' '}
                                    <span className="text-700">{v.queryText || 'No details provided'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderOcrDialog = () => {
        const successResults = individualResults.filter(r => r.success);
        const hasMultiple = successResults.length > 1;

        // Column header labels for each payslip
        const getPayslipHeader = (idx) => {
            const result = successResults[idx];
            const period = result?.extracted_data?.pay_period;
            if (period) return period;
            const name = result?.file_name || `Payslip ${idx + 1}`;
            return name.length > 15 ? name.substring(0, 12) + '...' : name;
        };

        const currencyBodyTemplate = (rowData, colKey) => {
            const val = rowData[colKey];
            if (!val || val === '-') return <span className="text-400">-</span>;

            // Auto-mask sensitive fields using centralized config
            const displayVal = maskFieldValue(rowData.field, String(val), isMasked);

            return <span className={rowData.isCurrency ? 'font-semibold' : ''}>{displayVal}</span>;
        };

        // Selectable body template — wraps currency template with click-to-select.
        // Reads from rowData._selIdx (embedded by tableData memo) so DataTable detects changes.
        // Auto-calc rows (gross/net/total_deductions) are NEVER selectable — they're derived from
        // line items; clicking them is a no-op and the visual hides the selection circle.
        const selectableBodyTemplate = (rowData, colIdx) => {
            const isAutoCalc = AUTO_CALC_FIELDS.includes(rowData.field);
            const isSelected = rowData._selIdx === colIdx;
            const val = rowData[`val_${colIdx}`];
            const hasVal = val && val !== '-';

            if (isAutoCalc) {
                // justify-content-end + tabular-nums: amounts hug the right edge and
                // line up digit-for-digit down the column.
                return (
                    <div className="ps-value-cell flex align-items-center justify-content-end gap-1 p-1" style={{ minHeight: '1.5rem' }}>
                        <i className="pi pi-calculator text-blue-400 text-xs" title="Auto-calculated from line items" />
                        {currencyBodyTemplate(rowData, `val_${colIdx}`)}
                    </div>
                );
            }

            return (
                <div
                    // No filled box on the selected cell — the blue dot beside the
                    // amount already marks the selection. Non-selected cells keep a
                    // faint hover so they still read as clickable.
                    className={`ps-value-cell flex align-items-center justify-content-end gap-1 cursor-pointer p-1 border-round ${hasVal && !isSelected ? 'hover:bg-blue-50' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!hasVal) return;
                        setSelectedValues(prev => {
                            if (prev[rowData.field] === colIdx) {
                                const updated = { ...prev };
                                delete updated[rowData.field];
                                return updated;
                            }
                            return { ...prev, [rowData.field]: colIdx };
                        });
                    }}
                    style={{ minHeight: '1.5rem' }}
                >
                    {hasVal && (
                        <i className={`pi text-xs ${isSelected ? 'pi-circle-fill text-blue-500' : 'pi-circle text-300'}`} />
                    )}
                    {currencyBodyTemplate(rowData, `val_${colIdx}`)}
                </div>
            );
        };

        // Percentage body template — plain input for currency rows (simple like Excel)
        // Reads from rowData._selIdx and rowData._pct (embedded by tableData memo)
        const percentageBodyTemplate = (rowData) => {
            if (rowData._selIdx === -1) {
                return <span className="text-300 text-xs">-</span>;
            }
            if (!rowData.isCurrency) {
                return <span className="text-400 text-xs">-</span>;
            }
            const pct = rowData._pct;
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <input
                        type="number"
                        key={`pct-${rowData.field}-${pct}`}
                        defaultValue={pct}
                        min={0}
                        max={200}
                        inputMode="numeric"
                        onBlur={(e) => {
                            const newVal = parseInt(e.target.value) || 100;
                            setPercentages(prev => ({ ...prev, [rowData.field]: newVal }));
                            }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                        className="text-xs text-center border-1 border-300 border-round surface-0"
                        style={{
                            width: '50px',
                            padding: '0.2rem 0.3rem',
                            outline: 'none',
                        }}
                    />
                    <span className="text-xs text-500 ml-1">%</span>
                </div>
            );
        };

        // Calculated value body template — reads from rowData._calcVal for line items,
        // and from calculatedValues for auto-calc rows (gross/net/total_deductions) so
        // those rows ALWAYS show a number instead of blank when selections shift.
        // Net Pay rows also surface a tooltip with the formula breakdown + a warning
        // chip if the calculated total diverges from the OCR-extracted figure.
        const calculatedBodyTemplate = (rowData) => {
            const isAutoCalc = AUTO_CALC_FIELDS.includes(rowData.field);
            // Read from rowData._calcVal so PrimeReact's per-row cache invalidates correctly
            // when tableData regenerates. tableData embeds the live composite for auto-calc rows.
            const val = rowData._calcVal;

            if (val === undefined || val === null) {
                return <span className="text-300 text-xs">-</span>;
            }
            if (rowData.isCurrency && typeof val === 'number') {
                if (isAutoCalc) {
                    const ocrVal = rowData.raw_0;
                    const drift = (typeof ocrVal === 'number') ? val - ocrVal : 0;
                    const isNetPay = rowData.field === 'net_pay' || rowData.field === 'net_salary';
                    const totalEarn = calculatedValues.total_earnings || calculatedValues.gross_salary || 0;
                    const totalDed = calculatedValues.total_deductions || 0;
                    const formula = isNetPay
                        ? `Net Pay = Earnings ${formatCurrency(totalEarn)} − Deductions ${formatCurrency(totalDed)} = ${formatCurrency(val)}`
                        : `Auto-calculated from selected line items: ${formatCurrency(val)}`;
                    return (
                        <div className="flex flex-column align-items-end gap-1" title={formula}>
                            <span className="font-bold text-blue-600 text-sm">{formatCurrency(val)}</span>
                            {Math.abs(drift) > 100 && (
                                <span className="text-orange-600 text-xs" title={`OCR reported ${formatCurrency(ocrVal)}`}>
                                    <i className="pi pi-exclamation-triangle mr-1 text-xs" />
                                    Differs from payslip by {formatCurrency(Math.abs(drift))}
                                </span>
                            )}
                        </div>
                    );
                }
                return <span className="font-bold text-blue-600 text-sm">{formatCurrency(val)}</span>;
            }
            const displayVal = maskFieldValue(rowData.field, String(val), isMasked);
            return <span className="font-semibold text-blue-600">{displayVal}</span>;
        };

        // Verification column body — verify tick / query flag + inline variance message
        const verificationBodyTemplate = (rowData) => {
            const fieldKey = rowData.field;
            const vEntry = verificationStatus[fieldKey];
            const status = vEntry?.status || null;
            const isAuto = vEntry?.auto || false;
            const queryText = vEntry?.queryText || '';

            return (
                <div className="flex flex-column gap-1">
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-check"
                            className={`p-button-rounded p-button-sm ${status === 'verified' ? 'p-button-success' : 'p-button-outlined p-button-secondary'}`}
                            style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.65rem' }}
                            tooltip={status === 'verified' && isAuto ? 'Auto-verified (consistent)' : 'Mark as verified'}
                            tooltipOptions={{ position: 'top' }}
                            aria-label="Mark as verified"
                            onClick={(e) => { e.stopPropagation(); handleVerification(fieldKey, 'verified'); }}
                        />
                        <Button
                            icon="pi pi-flag"
                            className={`p-button-rounded p-button-sm ${status === 'query' ? 'p-button-warning' : 'p-button-outlined p-button-secondary'}`}
                            style={{ width: '1.5rem', height: '1.5rem', fontSize: '0.65rem' }}
                            tooltip="Raise Query"
                            tooltipOptions={{ position: 'top' }}
                            aria-label="Raise query"
                            onClick={(e) => { e.stopPropagation(); handleVerification(fieldKey, 'query'); }}
                        />
                        {status === 'verified' && !isAuto && (
                            <i className="pi pi-check-circle text-green-500 text-xs" />
                        )}
                    </div>
                    {/* Inline variance message for rows with differing values — always visible */}
                    {rowData.hasVariance && (
                        <span className="text-orange-600 text-xs font-medium line-height-1">
                            <i className="pi pi-exclamation-triangle mr-1 text-xs" />
                            {rowData.varianceMsg || 'Values vary'}
                        </span>
                    )}
                    {/* Uncontrolled textarea — saves on blur so typing doesn't trigger re-render */}
                    {status === 'query' && (
                        <textarea
                            defaultValue={queryText}
                            onBlur={(e) => handleQueryTextChange(fieldKey, e.target.value)}
                            placeholder="Write your query reason..."
                            rows={2}
                            className="w-full border-1 border-300 border-round text-xs"
                            style={{ padding: '0.25rem 0.4rem', minWidth: '120px', resize: 'vertical', fontFamily: 'inherit' }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            );
        };

        // Verification summary counts for footer
        const verifiedCount = Object.values(verificationStatus).filter(v => v.status === 'verified').length;
        const queryCount = Object.values(verificationStatus).filter(v => v.status === 'query').length;
        const totalRows = comparisonData.length;

        // Inline mode skips the Dialog entirely. Render the body straight into the
        // parent (Sidebar/drawer) so we don't stack a 2nd modal on top of it.
        // Header + footer chrome are dropped in inline mode — the parent owns those.
        const dialogHeader = (
            <div className="flex align-items-center gap-2">
                <i className="pi pi-check-circle text-green-500" />
                <span>Payslip Data Extracted</span>
                {hasMultiple && (
                    <Tag value={`${successResults.length} payslips`} severity="info" className="text-xs" />
                )}
                {verifiedCount > 0 && (
                    <Tag value={`${verifiedCount}/${totalRows} verified`} severity="success" className="text-xs" />
                )}
                {queryCount > 0 && (
                    <Tag value={`${queryCount} queries`} severity="warning" className="text-xs" />
                )}
            </div>
        );
        const dialogFooter = (
            <div className="flex justify-content-between align-items-center">
                {/* Filled blue, label-only — reads as a sibling of Save rather than a
                    faint text link. Hover turns the label black (.payslip-close-btn). */}
                <Button label="Close" className="payslip-close-btn" onClick={handleDialogClose} />
                {/* Single "Save" button — replaces the old confusing pair
                    ("Save Verification" + "Use Values"). Save does the complete,
                    useful thing: persists the verification snapshot AND applies the
                    extracted values to the form, then closes (which triggers
                    offer-gen). The old "Save Verification"-only path was redundant
                    with this (Use Values already called saveVerificationData). */}
                <Button
                    label="Save"
                    icon="pi pi-save"
                    className="p-button-success"
                    disabled={Object.keys(calculatedValues).length === 0}
                    tooltip={Object.keys(calculatedValues).length === 0 ? 'Select values first' : `Save & apply ${Object.keys(calculatedValues).length} values to the form`}
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => {
                        // Use Default tab's snapshot for the wizard's form fields.
                        // Active lender tab values stay in lender_nth_selections and
                        // only drive per-lender offer math — never the wizard Net Salary field.
                        const { defaultCalculated } = buildDefaultSnapshot();
                        // Persist lender selections + Default to onFullOCRData BEFORE
                        // closing so the parent has the latest data when it triggers
                        // offer generation immediately after.
                        if (onFullOCRDataRef.current && ocrResult) {
                            saveVerificationData();
                        }
                        if (onOCRComplete) {
                            const fieldMappings = {};
                            Object.keys(defaultCalculated).forEach(key => {
                                fieldMappings[key] = defaultCalculated[key];
                            });
                            // 'use_values' source tells the parent this is the user
                            // explicitly confirming the OCR results — parent should
                            // close its drawer and trigger offer generation.
                            onOCRComplete(fieldMappings, defaultCalculated, {}, { source: 'use_values' });
                        }
                        handleDialogClose();
                    }}
                />
            </div>
        );

        const dialogBody = (
            <>
                {/* Preamble — the how-to note and the processing stat. Useful the
                    first time, noise on every subsequent look, so the same toggle
                    that hides the upload box hides these too. */}
                {uploadPanelOpen && hasMultiple && (
                    <Message severity="info" className="w-full mb-2" text="Review each field row-by-row. Verify or raise a query for discrepancies." />
                )}

                {uploadPanelOpen && ocrResult?.processing_time_ms > 0 && (
                    <div className="text-sm text-500 mb-2">
                        <i className="pi pi-clock mr-1" />
                        Processed in {ocrResult.processing_time_ms}ms
                        {ocrResult.files_processed > 1 && ` (${ocrResult.files_processed} files)`}
                    </div>
                )}

                {/* Analytics summary — sits ABOVE the lender tabs and the comparison
                    table so the headline counts (consistent / varies / verified /
                    queries) are the first thing read. It used to render at the very
                    bottom, after the table and the identity accordion, where it was
                    easy to miss entirely. */}
                {renderAnalytics()}

                {/* Per-Lender NTH tab strip. Default tab (index 0) shows the
                    borrower's full Net Pay (today's behavior). Lender tabs let
                    the DSA tick which earnings / deductions THAT lender counts
                    in NTH; the calc table re-renders for the active tab with
                    that lender's selections. Saved to leads.lender_nth_selections
                    on "Use Values" and consumed by the offer engine per-range. */}
                {comparisonData.length > 0 && (
                    <div className="surface-ground p-2 border-round mb-2">
                        <TabView
                            activeIndex={activeTabIndex}
                            onTabChange={(e) => switchToTab(e.index)}
                            scrollable
                            className="payslip-lender-tabs"
                        >
                            <TabPanel
                                header="Default"
                                leftIcon="pi pi-home mr-2"
                            />
                            {lenderTabs.map((tab, idx) => (
                                <TabPanel
                                    key={tab.lender_id}
                                    headerTemplate={(options) => (
                                        <div
                                            className={options.className}
                                            onClick={options.onClick}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="p-tabview-title">{tab.lender_name}</span>
                                            <i
                                                className="pi pi-times ml-2 text-500 hover:text-700"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    removeLenderTab(idx);
                                                }}
                                                aria-label={`Remove ${tab.lender_name} tab`}
                                                title={`Remove ${tab.lender_name}`}
                                            />
                                        </div>
                                    )}
                                />
                            ))}
                        </TabView>
                        <div className="flex align-items-center gap-2 mt-2">
                            <Dropdown
                                value={null}
                                options={availableLenders.filter(
                                    l => !lenderTabs.some(t => t.lender_id === l.id)
                                )}
                                optionLabel="name"
                                optionValue="id"
                                placeholder={loadingLenders ? 'Loading lenders…' : '+ Add Lender'}
                                onShow={ensureLendersLoaded}
                                onChange={(e) => {
                                    if (!e.value) return;
                                    const lender = availableLenders.find(l => l.id === e.value);
                                    addLenderTab(lender);
                                }}
                                disabled={loadingLenders}
                                filter
                                filterPlaceholder="Search lender…"
                                className="w-full md:w-20rem p-inputtext-sm"
                            />
                            {activeTabIndex > 0 && (
                                <Tag
                                    value={`${lenderTabs[activeTabIndex - 1]?.lender_name || ''} NTH (set by your selections)`}
                                    severity="info"
                                    className="text-xs"
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Calculation table — renders ONLY the Earnings / Deductions / Net Pay rows.
                    Identity rows (Company, Employee, PAN, UAN, Bank, Pay Period) live in a
                    collapsed Accordion below this table so they don't compete for vertical
                    space — the operator's main focus is the salary math. */}
                {/* Horizontal-scroll wrapper (bulletproof plain-HTML approach).
                    The table is forced to an explicit pixel width = sum of every
                    column, so it is always wider than a phone; this bounded
                    overflow-x container then scrolls it sideways so every payslip /
                    % / Calculated / Verify column is reachable on mobile. A plain
                    non-scrollable table inside an overflow div is used instead of
                    PrimeReact's `scrollable` mode because it reliably shows a native
                    horizontal scrollbar with the header scrolling in lockstep. */}
                <div className="w-full payslip-hscroll" style={{ maxWidth: '100%', minWidth: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {/* Force an always-visible horizontal scrollbar. Touch browsers
                    hide overlay scrollbars until you swipe, so the table looked
                    un-scrollable on mobile even though it scrolls. Explicit height
                    + -webkit-appearance:none makes the bar persistently visible and
                    draggable on Android/desktop Chrome and styled on Firefox. */}
                <style>{`
                    .payslip-hscroll {
                        scrollbar-width: thin;
                        scrollbar-color: rgba(0,0,0,0.4) rgba(0,0,0,0.08);
                    }
                    .payslip-hscroll::-webkit-scrollbar {
                        height: 10px;
                        -webkit-appearance: none;
                    }
                    .payslip-hscroll::-webkit-scrollbar-track {
                        background: rgba(0,0,0,0.06);
                        border-radius: 5px;
                    }
                    .payslip-hscroll::-webkit-scrollbar-thumb {
                        background: rgba(0,0,0,0.35);
                        border-radius: 5px;
                    }
                    .payslip-hscroll::-webkit-scrollbar-thumb:hover {
                        background: rgba(0,0,0,0.5);
                    }
                `}</style>
                <DataTable
                    key={`vt-${verificationVersion}`}
                    value={calcRows}
                    size="small"
                    showGridlines
                    stripedRows
                    tableStyle={{ minWidth: `${160 + (hasMultiple ? successResults.length * 130 : 150) + 80 + 120 + (hasMultiple ? 140 : 0)}px` }}
                    className="payslip-comparison-table"
                    rowGroupMode="subheader"
                    groupRowsBy="category"
                    sortField="categoryOrder"
                    sortOrder={1}
                    rowGroupHeaderTemplate={(data) => (
                        <span className="font-bold text-xs text-primary">
                            <i className={`pi ${FIELD_GROUPS.find(g => g.label === data.category)?.icon || 'pi-list'} mr-2 text-xs`} />
                            {data.category}
                        </span>
                    )}
                    rowClassName={(rowData) => {
                        const status = verificationStatus[rowData.field]?.status;
                        if (status === 'verified') return 'bg-green-50';
                        if (status === 'query') return 'bg-yellow-50';
                        if (rowData.hasVariance) return 'bg-orange-50';
                        return '';
                    }}
                    emptyMessage="No data extracted"
                >
                    <Column
                        field="label"
                        header="Field"
                        style={{ minWidth: '160px', maxWidth: '200px' }}
                        body={(rowData) => (
                            <div className="flex align-items-center gap-1">
                                <span className="text-xs font-medium text-700">{rowData.label}</span>
                                {rowData.hasVariance && (
                                    <i className="pi pi-exclamation-triangle text-orange-500 text-xs" title="Values differ across payslips" />
                                )}
                            </div>
                        )}
                    />
                    {hasMultiple && successResults.map((_, idx) => (
                        <Column
                            key={`payslip_${idx}`}
                            field={`val_${idx}`}
                            header={
                                <div className="flex align-items-center gap-1">
                                    <i className="pi pi-file text-primary text-xs" />
                                    <span className="text-xs white-space-nowrap font-semibold">{getPayslipHeader(idx)}</span>
                                </div>
                            }
                            style={{ minWidth: '130px', textAlign: 'right' }}
                            body={(rowData) => selectableBodyTemplate(rowData, idx)}
                        />
                    ))}
                    {!hasMultiple && (
                        <Column
                            field="val_0"
                            header="Value"
                            style={{ minWidth: '150px', textAlign: 'right' }}
                            body={(rowData) => selectableBodyTemplate(rowData, 0)}
                        />
                    )}
                    {/* Percentage Column */}
                    <Column
                        header={
                            // The standalone "%" caption that sat above the input was
                            // removed — the "%" suffix beside the field already labels
                            // it, so the header said it twice.
                            <div className="flex flex-column align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <div className="flex align-items-center gap-1">
                                    <input
                                        type="number"
                                        defaultValue={globalPercentage}
                                        min={0}
                                        max={200}
                                        inputMode="numeric"
                                        onBlur={(e) => {
                                            const newVal = parseInt(e.target.value) || 100;
                                            setGlobalPercentage(newVal);
                                            setPercentages(prev => {
                                                const updated = { ...prev };
                                                comparisonData.forEach(row => {
                                                    if (selectedValues[row.field] !== undefined && row.isCurrency) {
                                                        updated[row.field] = newVal;
                                                    }
                                                });
                                                return updated;
                                            });
                                                            }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                        className="text-xs text-center border-1 border-300 border-round surface-0"
                                        style={{
                                            width: '40px',
                                            padding: '0.15rem 0.2rem',
                                            outline: 'none',
                                        }}
                                    />
                                    <span className="text-xs text-500">%</span>
                                </div>
                            </div>
                        }
                        style={{ minWidth: '80px', textAlign: 'center' }}
                        body={percentageBodyTemplate}
                    />
                    {/* Calculated Value Column */}
                    <Column
                        header={
                            <div className="flex align-items-center gap-1">
                                <i className="pi pi-calculator text-blue-500 text-xs" />
                                <span className="text-xs font-bold text-blue-600">Calculated</span>
                            </div>
                        }
                        style={{ minWidth: '120px', textAlign: 'right' }}
                        body={calculatedBodyTemplate}
                    />
                    {/* Verification Column — replaces Average column */}
                    {hasMultiple && (
                        <Column
                            header={
                                <div className="flex align-items-center gap-1">
                                    <i className="pi pi-shield text-primary text-xs" />
                                    <span className="text-xs font-bold text-primary">Verify</span>
                                </div>
                            }
                            style={{ minWidth: '140px', textAlign: 'center' }}
                            body={verificationBodyTemplate}
                        />
                    )}
                </DataTable>
                </div>

                {/* Identity & static info — collapsed by default. PAN / UAN / Bank /
                    Company / Employee / Pay Period rarely need verification once OCR
                    extracts them; expanding gives the operator quick access without
                    eating vertical space when they're focused on the salary math. */}
                {identityRows.length > 0 && (
                    <Accordion className="mt-3">
                        <AccordionTab
                            header={
                                <span className="flex align-items-center gap-2 text-sm font-medium">
                                    <i className="pi pi-id-card text-primary" />
                                    Identity, Bank &amp; Pay Period
                                    {(() => {
                                        const varCount = identityRows.filter(r => r.hasVariance).length;
                                        if (varCount === 0) return null;
                                        return (
                                            <Tag
                                                value={`${varCount} differ`}
                                                severity="info"
                                                className="ml-2 text-xs ps-differ-tag"
                                            />
                                        );
                                    })()}
                                </span>
                            }
                        >
                            {(() => {
                                const groupedByCategory = identityRows.reduce((acc, row) => {
                                    (acc[row.category] = acc[row.category] || []).push(row);
                                    return acc;
                                }, {});
                                return Object.keys(groupedByCategory).map((catLabel) => {
                                    const groupMeta = FIELD_GROUPS.find(g => g.label === catLabel);
                                    const rows = groupedByCategory[catLabel];
                                    return (
                                        <div key={`identity-cat-${catLabel}`} className="mb-4">
                                            {/* Section header — badge + title with a rule running
                                                to the right edge, so groups separate at a glance. */}
                                            <div className="ps-idsec__head flex align-items-center gap-2 mb-2">
                                                <span className="ps-idsec__icon flex align-items-center justify-content-center border-round flex-shrink-0" aria-hidden="true">
                                                    <i className={`pi ${groupMeta?.icon || 'pi-list'}`} />
                                                </span>
                                                <span className="font-semibold text-base text-900 flex-shrink-0">{catLabel}</span>
                                                <span className="ps-idsec__rule flex-1" aria-hidden="true" />
                                            </div>
                                            <div className="grid">
                                                {rows.map((row) => {
                                                    // Pick first non-null raw value across payslips
                                                    let displayVal = null;
                                                    for (let i = 0; i < (hasMultiple ? successResults.length : 1); i++) {
                                                        const v = row[`raw_${i}`];
                                                        if (v !== null && v !== undefined && v !== '' && v !== 0) {
                                                            displayVal = v;
                                                            break;
                                                        }
                                                    }
                                                    if (displayVal === null || displayVal === undefined) return null;
                                                    const renderedVal = (row.isCurrency && typeof displayVal === 'number')
                                                        ? formatCurrency(displayVal)
                                                        : maskFieldValue(row.field, String(displayVal), isMasked);
                                                    const isExpanded = expandedIdentityDiffs.has(row.field);
                                                    // Build per-payslip value list (for the expanded diff view).
                                                    // We mark rows that differ from the first non-null reference
                                                    // so the operator can scan at a glance.
                                                    const perSlipValues = hasMultiple
                                                        ? successResults.map((r, i) => ({
                                                            label: getPayslipHeader(i),
                                                            raw: row[`raw_${i}`],
                                                        }))
                                                        : [];
                                                    const referenceVal = perSlipValues.find(p => p.raw !== null && p.raw !== undefined && p.raw !== '' && p.raw !== 0)?.raw;
                                                    return (
                                                        <div key={`identity-row-${row.field}`} className="col-12 md:col-6 lg:col-4">
                                                            {/* Icon badge left, label over value right —
                                                                the value is what matters, so it gets the
                                                                weight and the label recedes above it. */}
                                                            <div className={`ps-idcard ${row.hasVariance ? 'is-warn' : ''} flex align-items-start gap-2 p-2 border-round-lg h-full`}>
                                                                <span className="ps-idcard__icon flex align-items-center justify-content-center border-round flex-shrink-0" aria-hidden="true">
                                                                    <i className={`pi ${groupMeta?.icon || 'pi-file'}`} />
                                                                </span>
                                                                <div className="flex flex-column gap-1 min-w-0 flex-1">
                                                                <div className="flex align-items-center gap-1">
                                                                    <span className="text-xs text-500">{row.label}</span>
                                                                    {row.hasVariance && (
                                                                        <i
                                                                            className="pi pi-exclamation-triangle text-primary text-xs"
                                                                            title={row.varianceMsg || 'Values differ across payslips'}
                                                                        />
                                                                    )}
                                                                </div>
                                                                <span className="ps-idcard__value text-sm font-semibold text-900">{renderedVal}</span>
                                                                {row.hasVariance && row.varianceMsg && hasMultiple && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleIdentityDiff(row.field)}
                                                                        className="p-0 bg-transparent border-none cursor-pointer text-left text-primary text-xs flex align-items-center gap-1"
                                                                        aria-expanded={isExpanded}
                                                                        aria-label={`Show per-payslip values for ${row.label}`}
                                                                    >
                                                                        <i className={`pi text-xs ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                                                                        <span>{row.varianceMsg}</span>
                                                                    </button>
                                                                )}
                                                                {row.hasVariance && row.varianceMsg && !hasMultiple && (
                                                                    <span className="text-primary text-xs">{row.varianceMsg}</span>
                                                                )}
                                                                {isExpanded && perSlipValues.length > 0 && (
                                                                    <div className="mt-1 border-top-1 surface-border pt-1 flex flex-column gap-1">
                                                                        {perSlipValues.map((p, idx) => {
                                                                            const isEmpty = p.raw === null || p.raw === undefined || p.raw === '' || p.raw === 0;
                                                                            const isDifferent = !isEmpty && referenceVal !== undefined && p.raw !== referenceVal;
                                                                            const display = isEmpty
                                                                                ? '—'
                                                                                : (row.isCurrency && typeof p.raw === 'number')
                                                                                    ? formatCurrency(p.raw)
                                                                                    : maskFieldValue(row.field, String(p.raw), isMasked);
                                                                            return (
                                                                                <div key={`diff-${row.field}-${idx}`} className="flex justify-content-between align-items-start gap-2 text-xs">
                                                                                    <span className="text-500 white-space-nowrap">{p.label}:</span>
                                                                                    <span className={isDifferent ? 'text-primary font-semibold text-right' : 'text-700 text-right'}>
                                                                                        {display}
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </AccordionTab>
                    </Accordion>
                )}

            </>
        );

        // Inline mode: render the body directly into the parent (drawer/sidebar)
        // whenever there is extraction data — even if showOcrDialog is false (e.g.
        // a previously saved payslip restored from savedOcrData). We rely on
        // comparisonData being non-empty as the signal that there's something to show.
        if (inlineExtraction) {
            if (!comparisonData || comparisonData.length === 0) return null;
            return (
                <div className="payslip-extraction-inline">
                    {/* The "Payslip Data Extracted" strip restates what the success
                        banner above already said, so it collapses with the rest. */}
                    {uploadPanelOpen && <div className="mb-3">{dialogHeader}</div>}
                    {dialogBody}
                    <div className="mt-3 pt-3 border-top-1 surface-border">
                        {dialogFooter}
                    </div>
                </div>
            );
        }

        return (
            <Dialog
                visible={showOcrDialog}
                onHide={handleDialogClose}
                header={dialogHeader}
                style={{ width: '95vw', maxWidth: hasMultiple ? '1300px' : '850px' }}
                modal
                draggable={false}
                footer={dialogFooter}
            >
                {dialogBody}
            </Dialog>
        );
    };

    return (
        <div className="payslip-ocr-upload compact-ocr-section">
{/* Stepped progress indicator (Uploading → Analysing → Data Fetching → Data Formation).
                Replaces the prior single-bar UI so the DSA sees which stage the
                payslip is in, not just a generic "30%". Skill-compliant:
                  - PrimeFlex utilities only (no inline padding/margin, no new SCSS)
                  - PrimeIcons (pi-spin pi-spinner for active, pi-check-circle for done, pi-circle for pending)
                  - Mobile-first: stacks vertically on small viewports, becomes a horizontal row on md+
                  - Active stage label is text-primary; done is text-600; pending is text-500. */}
            {stepperStage != null && (() => {
                // stepperStage is driven directly by the lifecycle (see
                // processFilesWithOCR). Values:
                //   0 = Uploading         (file POST in flight)
                //   1 = Reading document  (OCR engine reading the document)
                //   2 = Extracting fields (the long Textract wait — no progress
                //                          events, so this is where it sits ~60s)
                //   3 = Preparing results (response received, mapping fields)
                const activeStep = stepperStage;

                // Labels match what actually happens at each phase. "Extracting
                // fields" is where the long Textract wait sits (see stage logic in
                // processFilesWithOCR), so the wording is honest about the slow step
                // rather than parking on a "Data Fetching" that sounds nearly done.
                const steps = [
                    { label: 'Uploading',        icon: 'pi-cloud-upload' },
                    { label: 'Reading document', icon: 'pi-search' },
                    { label: 'Extracting fields',icon: 'pi-list' },
                    { label: 'Preparing results',icon: 'pi-check-square' },
                ];

                return (
                    <div className="mb-2 p-3 surface-100 border-round">
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-spin pi-spinner text-primary" aria-hidden="true" />
                            <span className="font-medium text-sm text-900">
                                Processing {selectedFiles.length > 1 ? `${selectedFiles.length} payslips` : 'payslip'} with AI
                            </span>
                        </div>
                        <div className="flex flex-column md:flex-row md:align-items-center gap-2 md:gap-3">
                            {steps.map((s, idx) => {
                                const isDone   = idx <  activeStep;
                                const isActive = idx === activeStep;
                                const iconClass = isDone
                                    ? 'pi pi-check-circle text-green-500'
                                    : isActive
                                        ? 'pi pi-spin pi-spinner text-primary'
                                        : 'pi pi-circle text-400';
                                const labelClass = isDone
                                    ? 'text-sm text-600'
                                    : isActive
                                        ? 'text-sm font-medium text-primary'
                                        : 'text-sm text-500';
                                return (
                                    <React.Fragment key={s.label}>
                                        <div className="flex align-items-center gap-2 flex-1">
                                            <i className={iconClass} aria-hidden="true" />
                                            <span className={labelClass}>{s.label}</span>
                                        </div>
                                        {/* Connector line between steps — only on md+ horizontal layout */}
                                        {idx < steps.length - 1 && (
                                            <div
                                                className={`hidden md:block border-top-2 ${isDone ? 'border-green-500' : 'border-300'}`}
                                                style={{ width: '1.5rem' }}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Indeterminate bar + live elapsed seconds. Textract sends no
                            progress during the read, so a determinate bar would sit
                            still and look hung — an always-moving indeterminate bar
                            plus a ticking counter tell the user it is genuinely working. */}
                        <ProgressBar
                            mode="indeterminate"
                            style={{ height: '4px' }}
                            className="mt-3"
                        />
                        <div className="flex align-items-center justify-content-between mt-2">
                            <span className="text-xs text-500">
                                {activeStep >= 2
                                    ? 'Reading the payslip can take up to a minute — please wait.'
                                    : 'Getting your payslip ready…'}
                            </span>
                            <span className="text-xs text-600 font-medium" aria-live="polite">
                                {ocrElapsedSec}s
                            </span>
                        </div>
                    </div>
                );
            })()}

            {/* OCR Error */}
            {ocrError && !ocrProcessing && (
                <Message severity="warn" className="w-full mb-2 text-xs" content={
                    <div className="flex align-items-center gap-1 text-xs">
                        <i className="pi pi-exclamation-triangle text-xs" />
                        <span>{ocrError}</span>
                    </div>
                } />
            )}

            {/* OCR Success Badge — collapses with the rest of the preamble. It also
                carries View Payslips / View Data, which is why the toggle's label
                warns that actions are hidden too. */}
            {ocrResult && !ocrProcessing && uploadPanelOpen && (
                <div className="mb-2 p-1 border-round flex align-items-center justify-content-between bg-green-50 border-1 border-green-200">
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-check-circle text-green-500 text-xs" />
                        <span className="text-green-700 font-medium text-xs">
                            {individualResults.length > 1
                                ? `${individualResults.filter(r => r.success).length} payslips extracted & compared`
                                : 'OCR data extracted successfully'}
                        </span>
                    </div>
                    <div className="flex gap-1">
                        {viewablePayslips.length > 0 && (
                            <Button
                                label={`View Payslip${viewablePayslips.length > 1 ? 's' : ''}`}
                                icon="pi pi-file"
                                className="p-button-text p-button-sm p-button-info"
                                onClick={() => {
                                    // Open the inline viewer dialog at payslip 1.
                                    // The dialog handles S3 auth + prev/next nav
                                    // through ALL uploaded payslips. Switched
                                    // from forEach(window.open) which only
                                    // opened 1 of N because browser popup
                                    // blockers ate the rest after the first
                                    // await resolved (user gesture expired).
                                    setViewingPayslipIndex(0);
                                    setShowPayslipViewer(true);
                                }}
                            />
                        )}
                        {/* View Data button kept in both modes. In inline mode the extraction
                            already shows below — this button just toggles its highlight/scroll. */}
                        <Button label="View Data" icon="pi pi-eye" className="p-button-text p-button-sm p-button-success" onClick={() => setShowOcrDialog(true)} />
                    </div>
                </div>
            )}

            {/* Standalone View Payslips button when OCR banner is not shown but viewable files exist */}
            {!ocrResult && viewablePayslips.length > 0 && (
                <div className="mb-2 p-1 border-round flex align-items-center justify-content-between bg-blue-50 border-1 border-blue-200">
                    <div className="flex align-items-center gap-1">
                        <i className="pi pi-file text-blue-500 text-xs" />
                        <span className="text-blue-700 font-medium text-xs">
                            {viewablePayslips.length} payslip{viewablePayslips.length > 1 ? 's' : ''} uploaded
                        </span>
                    </div>
                    <Button
                        label={`View Payslip${viewablePayslips.length > 1 ? 's' : ''}`}
                        icon="pi pi-eye"
                        className="p-button-text p-button-sm p-button-info"
                        onClick={() => {
                            // Open inline viewer dialog at payslip 1; DSA
                            // pages through prev/next inside the dialog.
                            setViewingPayslipIndex(0);
                            setShowPayslipViewer(true);
                        }}
                    />
                </div>
            )}

            {/* File Upload — a dashed drop zone rather than a bare button, so the
                area reads as a target for a file. The three constraints are stated
                up front instead of being discovered by failing an upload. */}
            {/* Once data has been extracted the big drop zone collapses to a single
                compact button. The zone is the primary action on an empty panel, but
                afterwards the extracted table is what matters — leaving a full-height
                target above it just pushes the results down the page.
                It stays available (not removed) so payslips 2 and 3 can still be
                added. */}
            {/* Toggle — the one control that hides or reveals the upload area. Its
                label states what will happen and why, so it isn't a bare chevron
                the user has to experiment with. */}
            {/* Deliberately NOT gated on canUploadMore — once all 3 payslips are in,
                that goes false, and gating here would remove the only control that
                can reopen a collapsed panel. */}
            {ocrResult && !hideUpload && !ocrProcessing && (
                <div className="flex justify-content-center mb-2">
                    <Button
                        label={uploadPanelOpen
                            ? 'Hide upload & details'
                            : 'Show upload & details'}
                        icon={uploadPanelOpen ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                        className="p-button-text p-button-sm"
                        aria-expanded={uploadPanelOpen}
                        aria-controls="payslip-upload-area"
                        tooltip={uploadPanelOpen
                            ? 'Collapse the upload box, status banner and notes so the comparison table gets the full panel'
                            : 'Reopen the upload box, View Payslips / View Data and the extraction notes'}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => setUploadPanelOpen((v) => !v)}
                    />
                </div>
            )}

            {/* Compact picker — shown only while the panel is open AND data already
                exists, so the big drop zone isn't repeated after extraction. */}
            {ocrResult && uploadPanelOpen && canUploadMore && !hideUpload && !ocrProcessing && (
                <div id="payslip-upload-area" className="flex justify-content-center mb-2">
                    <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        accept={allowedFormats.map(f => `.${f}`).join(',')}
                        maxFileSize={maxFileSize}
                        chooseLabel={isUploading
                            ? `Uploading ${uploadProgress}%…`
                            : `Add another payslip (${uploadedFiles.length}/${MAX_PAYSLIPS})`}
                        auto
                        multiple
                        disabled={disabled || isUploading || ocrProcessing}
                        customUpload
                        uploadHandler={handleFileSelect}
                        onValidationFail={handleUploadValidationFail}
                        chooseOptions={{ icon: 'pi pi-plus', className: 'p-button-outlined p-button-sm' }}
                    />
                </div>
            )}

            {!ocrResult && canUploadMore && !hideUpload && !ocrProcessing && (
                <div className="payslip-dropzone border-round-lg p-3">
                    <div className="flex flex-column md:flex-row align-items-center justify-content-center gap-3 md:gap-4">
                        <div className="flex flex-column align-items-center gap-2">
                            <span className="payslip-dropzone__icon flex align-items-center justify-content-center border-circle" aria-hidden="true">
                                <i className="pi pi-cloud-upload" />
                            </span>
                            <span className="text-sm text-700 font-medium">Drop your payslips here</span>
                            <span className="text-xs text-500">or</span>
                            <FileUpload
                                ref={fileUploadRef}
                                mode="basic"
                                accept={allowedFormats.map(f => `.${f}`).join(',')}
                                maxFileSize={maxFileSize}
                                chooseLabel={isUploading ? `Uploading ${uploadProgress}%...` : `Upload Payslip (up to ${MAX_PAYSLIPS})`}
                                auto
                                multiple
                                disabled={disabled || isUploading || ocrProcessing}
                                customUpload
                                uploadHandler={handleFileSelect}
                                onValidationFail={handleUploadValidationFail}
                                chooseOptions={{ icon: 'pi pi-cloud-upload', className: 'payslip-dropzone__btn p-button-sm' }}
                            />
                        </div>
                        <ul className="list-none p-0 m-0 flex flex-column gap-2">
                            <li className="flex align-items-center gap-2 text-xs text-600">
                                <i className="pi pi-check text-primary text-xs" aria-hidden="true" />
                                <span>{allowedFormats.map(f => f.toUpperCase()).join(', ')}</span>
                            </li>
                            <li className="flex align-items-center gap-2 text-xs text-600">
                                <i className="pi pi-check text-primary text-xs" aria-hidden="true" />
                                <span>Max {Math.round(maxFileSize / (1024 * 1024))}MB per file</span>
                            </li>
                            <li className="flex align-items-center gap-2 text-xs text-600">
                                <i className="pi pi-check text-primary text-xs" aria-hidden="true" />
                                <span>Up to {MAX_PAYSLIPS} files</span>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Upload-only progress bar removed 2026-05-27: the stepped-progress
                block above already covers the upload phase (activeStep = 0) so
                a separate ProgressBar here would be a duplicate. */}

            {/* Payslip Document Viewer Dialog */}
            <Dialog
                visible={showPayslipViewer}
                onHide={() => {
                    setShowPayslipViewer(false);
                    // Revoke cached blob URLs to free memory
                    Object.values(s3BlobUrls).forEach(blobUrl => {
                        try { URL.revokeObjectURL(blobUrl); } catch {}
                    });
                    setS3BlobUrls({});
                }}
                onShow={() => {
                    // Pre-fetch the current payslip as blob URL if it's an S3 file (not image).
                    // Uses fetchDocumentBlob so auth headers are sent and failures don't loop.
                    const ps = viewablePayslips[viewingPayslipIndex];
                    if (ps?.source === 's3' && ps?.url && !s3BlobUrls[ps.url]) {
                        const name = (ps.name || '').toLowerCase();
                        const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)/.test(name.split('?')[0]);
                        if (!isImage) {
                            setLoadingS3Doc(true);
                            fetchDocumentBlob(ps.url).then(blobUrl => {
                                if (blobUrl) setS3BlobUrls(prev => ({ ...prev, [ps.url]: blobUrl }));
                            }).finally(() => setLoadingS3Doc(false));
                        }
                    }
                }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className={`pi ${loadingS3Doc ? 'pi-spinner pi-spin' : 'pi-file'} text-primary`} />
                        <span>
                            {viewablePayslips.length > 1
                                ? `Payslip ${viewingPayslipIndex + 1} of ${viewablePayslips.length}`
                                : 'Payslip Document'}
                        </span>
                        {viewablePayslips[viewingPayslipIndex]?.name && (
                            <span className="text-500 font-normal text-sm ml-1">
                                — {viewablePayslips[viewingPayslipIndex].name}
                            </span>
                        )}
                    </div>
                }
                style={{ width: '90vw', height: '90vh' }}
                maximizable
                modal
                contentStyle={{ padding: 0, height: 'calc(90vh - 100px)' }}
                footer={
                    <div className="flex justify-content-between align-items-center">
                        <div className="flex gap-2">
                            {viewablePayslips.length > 1 && (
                                <>
                                    <Button
                                        label="Previous"
                                        icon="pi pi-chevron-left"
                                        className="p-button-outlined p-button-sm"
                                        disabled={viewingPayslipIndex === 0}
                                        onClick={() => {
                                            const newIdx = viewingPayslipIndex - 1;
                                            setViewingPayslipIndex(newIdx);
                                            // Pre-fetch if S3 source
                                            const ps = viewablePayslips[newIdx];
                                            if (ps?.source === 's3' && ps?.url && !s3BlobUrls[ps.url]) {
                                                const name = (ps.name || '').toLowerCase();
                                                if (!/\.(jpg|jpeg|png|gif|bmp|webp)/.test(name.split('?')[0])) {
                                                    setLoadingS3Doc(true);
                                                    fetchDocumentBlob(ps.url).then(blobUrl => {
                                                        if (blobUrl) setS3BlobUrls(prev => ({ ...prev, [ps.url]: blobUrl }));
                                                    }).finally(() => setLoadingS3Doc(false));
                                                }
                                            }
                                        }}
                                    />
                                    <Button
                                        label="Next"
                                        icon="pi pi-chevron-right"
                                        iconPos="right"
                                        className="p-button-outlined p-button-sm"
                                        disabled={viewingPayslipIndex >= viewablePayslips.length - 1}
                                        onClick={() => {
                                            const newIdx = viewingPayslipIndex + 1;
                                            setViewingPayslipIndex(newIdx);
                                            // Pre-fetch if S3 source
                                            const ps = viewablePayslips[newIdx];
                                            if (ps?.source === 's3' && ps?.url && !s3BlobUrls[ps.url]) {
                                                const name = (ps.name || '').toLowerCase();
                                                if (!/\.(jpg|jpeg|png|gif|bmp|webp)/.test(name.split('?')[0])) {
                                                    setLoadingS3Doc(true);
                                                    fetchDocumentBlob(ps.url).then(blobUrl => {
                                                        if (blobUrl) setS3BlobUrls(prev => ({ ...prev, [ps.url]: blobUrl }));
                                                    }).finally(() => setLoadingS3Doc(false));
                                                }
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </div>
                        <Button
                            label="Open in New Tab"
                            icon="pi pi-external-link"
                            className="p-button-text p-button-sm"
                            onClick={() => {
                                const ps = viewablePayslips[viewingPayslipIndex];
                                const url = s3BlobUrls[ps?.url] || ps?.url;
                                if (url) window.open(url, '_blank');
                            }}
                        />
                    </div>
                }
            >
                {viewablePayslips[viewingPayslipIndex]?.url && (
                    (() => {
                        const currentPayslip = viewablePayslips[viewingPayslipIndex];
                        const originalUrl = currentPayslip.url;
                        const name = (currentPayslip.name || '').toLowerCase();
                        // Check file type from File object, persisted type, or file extension
                        const isImage = currentPayslip._file
                            ? currentPayslip._file.type.startsWith('image/')
                            : currentPayslip.type
                                ? currentPayslip.type.startsWith('image/')
                                : /\.(jpg|jpeg|png|gif|bmp|webp)/.test(name.split('?')[0]);

                        if (isImage) {
                            return (
                                <div className="flex justify-content-center align-items-center h-full p-3 overflow-auto">
                                    <img src={originalUrl} alt="Payslip" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                </div>
                            );
                        }

                        // For S3 files, use cached blob URL to avoid CSP blocking
                        const displayUrl = (currentPayslip.source === 's3') ? (s3BlobUrls[originalUrl] || null) : originalUrl;

                        if (loadingS3Doc && !displayUrl) {
                            return (
                                <div className="flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
                                    <i className="pi pi-spinner pi-spin pi-2x text-primary mb-3" />
                                    <span className="text-color-secondary">Loading document...</span>
                                </div>
                            );
                        }

                        if (!displayUrl) {
                            // Permanently-failed URL → show error instead of looping.
                            if (currentPayslip.source === 's3' && failedFetchUrls.current.has(originalUrl)) {
                                return (
                                    <div className="flex flex-column justify-content-center align-items-center text-center" style={{ height: '100%' }}>
                                        <i className="pi pi-exclamation-triangle pi-2x text-orange-500 mb-3" />
                                        <span className="text-color-secondary">Preview unavailable.</span>
                                        <span className="text-500 text-sm mt-1">The document is uploaded — extraction continued.</span>
                                    </div>
                                );
                            }
                            // Not yet fetched and not previously failed → trigger fetch.
                            // The guard against re-triggering after failure is in fetchDocumentBlob
                            // (failedFetchUrls.current.has check). The loadingS3Doc state is the
                            // additional belt against firing multiple parallel fetches for the same URL.
                            if (currentPayslip.source === 's3' && !loadingS3Doc) {
                                setLoadingS3Doc(true);
                                fetchDocumentBlob(originalUrl).then(blobUrl => {
                                    if (blobUrl) setS3BlobUrls(prev => ({ ...prev, [originalUrl]: blobUrl }));
                                }).finally(() => setLoadingS3Doc(false));
                            }
                            return (
                                <div className="flex flex-column justify-content-center align-items-center" style={{ height: '100%' }}>
                                    <i className="pi pi-spinner pi-spin pi-2x text-primary mb-3" />
                                    <span className="text-color-secondary">Loading document...</span>
                                </div>
                            );
                        }

                        return (
                            <iframe
                                src={displayUrl}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                                title="Payslip Document"
                            />
                        );
                    })()
                )}
            </Dialog>

            {renderOcrDialog()}

            <div className="text-color-secondary mt-1 flex align-items-center gap-1 text-xs">
                <i className="pi pi-info-circle text-xs" />
                <span>Upload up to {MAX_PAYSLIPS} payslips — values compared side-by-side with analytics</span>
            </div>
        </div>
    );
};

export default PayslipOCRUpload;
